/**
 * EpubReaderController.js — v2
 *
 * In-bio EPUB reader. Loads epub.js (global `ePub`) from CDN via index.html,
 * renders an overlay reader inside the bio panel, persists per-character
 * location/bookmarks/highlights/settings to localStorage, and pushes a rich
 * book context (visible page text + prev/next tail + selection + location
 * anchor) into `window.ActiveBookContext` so /api/chat/stream can attach it
 * as `book_context`.
 *
 * v2 features:
 *   - Smart context: visible CFI range, prev/next surrounding text within
 *     the chapter, user selection promoted as focal context, stable location
 *     anchor (chapter, %-through-chapter, page X of Y), diff hash so the
 *     server can short-circuit when nothing changed.
 *   - Read-aloud (Play / Pause / Stop) using the avatar's existing
 *     RealtimeLLMToElevenLabs pipeline so lip-sync works. Sentence-level
 *     highlight overlay tracks the spoken sentence.
 *   - "Aa" settings popover: brightness, line-spacing, margin width,
 *     justification, single/double-column toggle (in addition to font size,
 *     family, theme).
 *   - Progress scrubber with chapter tooltip.
 *   - Bookmarks side drawer (browseable, add/remove from list).
 *   - Highlights with colors + notes (saved per character, replayed on load).
 *   - Dictionary tap-to-define (Wiktionary REST + in-memory cache).
 *   - Search results drawer with chapter + snippet context.
 *   - Time-left-in-chapter estimate next to the page counter.
 */

// Reading location, bookmarks and highlights are all scoped per BOOK
// (characterId + book key) so a character with several EPUBs keeps a separate
// reading position / saved passages / highlights for each one. Earlier builds
// scoped loc + bookmarks per-character only; a guarded one-time migration
// (see _migrateLegacyPerCharacterData) moves that data onto the per-book key
// when the character has exactly one book (so the attribution is unambiguous).
const LS = {
    loc:        (cid, bk) => `epubReader:loc:${cid}${bk ? ":" + bk : ""}`,
    marks:      (cid, bk) => `epubReader:bookmarks:${cid}${bk ? ":" + bk : ""}`,
    highlights: (cid, bk) => `epubReader:highlights:${cid}${bk ? ":" + bk : ""}`,
    prefs:      ()    => `epubReader:prefs`,
    // Per-book whole-book progress fraction (0..1), keyed identically to loc so
    // the library cards can show progress without re-opening each book.
    progress:   (cid, bk) => `epubReader:progress:${cid}${bk ? ":" + bk : ""}`,
};

const DEFAULT_PREFS = {
    fontSize: 110,            // 70-180 (%)
    fontFamily: "serif",      // serif | sans
    theme: "parchment",       // parchment | sepia | quiet | light | dark
    lineHeight: 1.5,          // 1.2 / 1.5 / 1.85
    marginPct: 8,             // 4 / 8 / 14 (% of page width as side padding)
    justify: "justify",       // left | justify
    columns: "auto",          // auto | single
    brightness: 1.0,          // 0.4 .. 1.3 (embedded uses CSS filter; legacy uses overlay alpha)
    flow: "paginated",        // paginated | scrolled-doc
};

const READING_WPM = 220;
// Read-aloud word follow-along: ElevenLabs does not expose per-word audio
// timestamps to clients, so the karaoke word highlight estimates each word's
// duration by distributing the sentence's spoken audio window across its words,
// weighted by character length. ~15 chars/sec ≈ natural English narration pace.
const READ_ALOUD_CHARS_PER_SEC = 15;
const READ_ALOUD_MIN_WORD_MS = 90;
// Highlight palette tuned to the museum ("Tuscan dusk") look — solid SVG `fill`
// colors for the in-iframe annotation, a CSS class for the menu swatch's
// gradient, and a human label used for the hover tooltip.
const HIGHLIGHT_COLORS = [
    { id: "amber", color: "#d4a058", cls: "sel-color-amber", tip: "Highlight (amber)" },
    { id: "rose",  color: "#c8645c", cls: "sel-color-rose",  tip: "Highlight (rose)" },
    { id: "teal",  color: "#3d8a87", cls: "sel-color-teal",  tip: "Highlight (teal)" },
    { id: "sage",  color: "#88a070", cls: "sel-color-sage",  tip: "Highlight (sage)" },
];

export class EpubReaderController {
    constructor() {
        this.bookRegistry = null;
        this.registryPromise = null;
        this.activeCharacterId = null;
        this.activeBookMeta = null;
        this.activeBookKey = "";
        this.book = null;
        this.rendition = null;
        this.locations = null;
        this.locationsReady = false;
        this.prefs = this._loadPrefs();
        this.searchResults = [];
        this.isOpen = false;
        this._tocItems = [];
        this._navToc = [];
        this.embedded = false;          // true when mounted inside the lab right-panel
        this._embedHost = null;
        this._lastCfiKey = "";          // diff key for context publish
        this._dictCache = new Map();
        this._readAloud = {
            state: "idle",              // idle | playing | paused | stopping
            queue: [],                  // [{text, range}]
            index: 0,                   // index of currently-speaking sentence
            cfi: "",                    // CFI of current visible page when started
        };
        this._activeWordRange = null;   // DOM range of the currently-spoken word
        this._wordPacer = null;         // setTimeout id for the karaoke word stepper
        this._buildOverlay();
        this._wireGlobalKeys();
    }

    async getBooksForCharacter(characterId) {
        if (!characterId) return [];
        if (!this._booksByCharacter) this._booksByCharacter = new Map();
        if (this._booksByCharacter.has(characterId)) {
            return this._booksByCharacter.get(characterId);
        }
        const p = fetch(`/api/character_books/${encodeURIComponent(characterId)}`,
            { cache: "no-cache" })
            .then((r) => (r.ok ? r.json() : { books: [] }))
            .then((j) => (Array.isArray(j && j.books) ? j.books : []))
            .catch((err) => {
                console.warn("[EpubReader] failed to load books for", characterId, err);
                return [];
            });
        this._booksByCharacter.set(characterId, p);
        return p;
    }

    /** Backward-compatible single-book lookup. */
    async getBookForCharacter(characterId, bookId) {
        const books = await this.getBooksForCharacter(characterId);
        if (!books.length) return null;
        if (bookId) {
            const match = books.find((b) => b.id === bookId);
            if (match) return match;
        }
        return books[0];
    }

    async open(characterId, characterName, bookId, opts = {}) {
        if (typeof window.ePub !== "function") {
            console.error("[EpubReader] epub.js not loaded — cannot open reader");
            return;
        }
        // Demo deep link (&book=<slug>&page=<n>): page target either passed
        // directly or armed as a ONE-SHOT global by the /lab deep-link driver
        // right before it clicks the book card (the click path keeps all the
        // normal user semantics). Consumed synchronously so a later unrelated
        // open() can never inherit it.
        let deepPage = Math.max(0, (opts && opts.page) | 0);
        if (!deepPage && typeof window !== "undefined" && (window.__llDeepBookPage | 0) > 0) {
            deepPage = window.__llDeepBookPage | 0;
            try { delete window.__llDeepBookPage; } catch (_) { window.__llDeepBookPage = 0; }
        }
        // Mount + show the spinner instantly, BEFORE the (possibly cold) book
        // lookup network call, so the click feels responsive.
        this._mountOverlay();
        this.overlay.classList.add("is-open");
        this.overlay.dataset.theme = this.prefs.theme;
        this.isOpen = true;
        this._setLoading(true, "Loading book…");
        this._setError("");

        // Per-open generation token + abort ownership FIRST — before ANY
        // await. The metadata lookup below is a network call: without taking
        // the generation up front, a SLOWER earlier open() could resume after
        // a faster later click, out-rank it here and open the wrong book.
        // Every await below must bail out once a newer open() (or close())
        // has taken over.
        if (this._bookFetchAbort) {
            try { this._bookFetchAbort.abort(); } catch (_) {}
        }
        const controller = (typeof AbortController === "function")
            ? new AbortController() : null;
        this._bookFetchAbort = controller;
        this._openId = (this._openId || 0) + 1;
        const openId = this._openId;
        const isStale = () => this._openId !== openId;

        const meta = await this.getBookForCharacter(characterId, bookId);
        if (isStale()) return; // a newer open()/close() owns the UI now
        if (!meta) {
            console.warn("[EpubReader] No book registered for character", characterId);
            this._setLoading(false);
            this._setError("No book is available for this legend yet.");
            return;
        }
        this.activeCharacterId = characterId;
        this.activeBookMeta = { ...meta, characterId, characterName: characterName || "" };
        this.activeBookKey = this._bookKey(this.activeBookMeta);
        this._lastJournalHref = null; // reset per-open so book_read tracks this book's chapters
        this._migrateLegacyPerCharacterData(characterId);
        // Whole-book ETA is per-book; reset its cache so a previously-opened
        // book's word counts never bleed into this one (singleton controller).
        this._resetBookWordCounts();

        if (!this._playPoll) {
            this._playPoll = setInterval(() => {
                if (!this.isOpen) return;
                this._updatePlayButton();
            }, 750);
        }

        try {
            this.stopReadAloud();

            // epub.js 0.3.93 resolves a book's internal package paths
            // (content.opf, chapter XHTML, etc.) relative to the URL it's
            // handed. Filenames with spaces/parens/commas (and the old
            // Firebase signed URLs' long `?X-Goog-Signature=…` query strings)
            // break epub.js's relative-URL resolution — `book.ready` and
            // `rendition.display()` never resolve and the spinner hangs.
            // Books now come from `/static/epubs/...`; we fetch the EPUB
            // ourselves as an ArrayBuffer and hand the binary buffer to ePub()
            // so it skips the fragile URL-resolution path entirely. (Signed
            // URLs are still handled the same way for safety.)
            const src = meta.url || meta.file;
            if (!src) throw new Error("missing-url");

            const looksLikeSignedUrl = /^https?:/i.test(src)
                && (src.indexOf("?") !== -1 || /storage\.googleapis\.com|firebasestorage\.googleapis\.com/i.test(src));
            const looksLikeLocalEpub = /\.epub(\?|$)/i.test(src);
            // Runtime library books stream from /api/library/file/<id> — a
            // URL with NO .epub suffix. Handed that URL directly, epub.js
            // treats it as an UNPACKED book directory, strips the last path
            // segment and requests /api/library/file/META-INF/container.xml
            // (404) — the book never opens. They are always archives, so
            // route them through the same ArrayBuffer path (binary input is
            // unambiguous to epub.js and keeps our timeout/abort/error taxonomy).
            const looksLikeLibraryBook = (meta.source === "library")
                || /^\/api\/library\/file\//.test(src);

            let bookInput;
            if (looksLikeSignedUrl || looksLikeLocalEpub || looksLikeLibraryBook) {
                bookInput = await this._fetchBookBuffer(src, controller);
            } else {
                bookInput = src;
            }
            if (isStale()) { const e = new Error("abort"); e.code = "abort"; throw e; }

            // Tear down the previous reader ONLY now that we're committed to
            // building its replacement. Doing this earlier (on entry) meant a
            // failed/aborted fetch above would leave `this.rendition` pointing
            // at a destroyed rendition — every subsequent next()/prev() then
            // threw "undefined is not an object (this.hooks.content)" and the
            // book appeared stuck on page one. Destroy→renderTo is synchronous
            // (no await between), so no other open() can interleave here.
            if (this.rendition) { try { this.rendition.destroy(); } catch (_) {} this.rendition = null; }
            if (this.book) { try { this.book.destroy(); } catch (_) {} this.book = null; }

            this.book = window.ePub(bookInput);
            this.rendition = this.book.renderTo(this.areaEl, {
                width: "100%",
                height: "100%",
                flow: this.prefs.flow || "paginated",
                manager: "default",
                allowScriptedContent: true,
                spread: this.prefs.columns === "single" ? "none" : "auto",
            });

            this._applyPrefsToRendition();
            this._registerContentHooks();
            this._wireRenditionEvents();

            // Scholar's Journal: record the open, and pull account reading state
            // (location/progress/highlights/bookmarks) into localStorage BEFORE
            // we restore the saved position below, so a signed-in user resumes
            // where they left off on any device.
            try { window.logJournalActivity?.('book_open', { meta: { book: meta.title || this.activeBookKey } }); } catch (e) {}
            await this._loadReadingStateFromAccount();
            if (isStale()) { const e = new Error("abort"); e.code = "abort"; throw e; }

            // Deep-link opens ignore the saved last-location: the URL must
            // reproduce the same page every time, on any profile.
            const savedLoc = deepPage
                ? null
                : localStorage.getItem(LS.loc(characterId, this.activeBookKey));
            await this._withTimeout(
                this.rendition.display(savedLoc || undefined),
                30000,
                "display-timeout"
            );
            if (isStale()) { const e = new Error("abort"); e.code = "abort"; throw e; }

            // Now that content is displayed, re-measure the stage and keep it in
            // sync with the resizable panel. The panel widens (setPanelWidth) and
            // animates open AFTER renderTo first measured, so without this the
            // columns are laid out for the wrong width → text overlaps / clips
            // and font-size changes look like they do nothing.
            this._ensureAreaResizeObserver();
            this._ensureParentSelectionWatch();
            this._ensureSelectionPoll();
            try { this.rendition.resize(); } catch (_) {}

            this.titleEl.textContent = `${meta.title} — ${meta.author}`;

            await this._withTimeout(this.book.ready, 30000, "ready-timeout");
            if (isStale()) { const e = new Error("abort"); e.code = "abort"; throw e; }
            const nav = await this.book.loaded.navigation;
            if (isStale()) { const e = new Error("abort"); e.code = "abort"; throw e; }
            this._renderToc(nav);

            this.book.locations.generate(1600).then(async () => {
                if (isStale()) return;
                this.locationsReady = true;
                if (deepPage) {
                    // Deep link: land on page n of real prose (never the TOC),
                    // now that reading-position locations exist.
                    try { await this._gotoProsePage(deepPage, isStale); } catch (_) {}
                    if (isStale()) return;
                }
                this._updateFooter(this.rendition.currentLocation());
                this._updateScrubber(this.rendition.currentLocation());
                this._emitRelocated(this.rendition.currentLocation());
            }).catch(() => {});

            this._loadAndReplayAllHighlights();

            this._setLoading(false);

            this._updatePlayButton();

            this._publishContext({ initial: true });
            window.dispatchEvent(new CustomEvent("bookReaderOpened", {
                detail: { characterId, book: meta },
            }));
        } catch (err) {
            console.error("[EpubReader] Failed to open book", err);
            // Don't overwrite UI if a newer open() already took over.
            if (!isStale()) {
                this._setLoading(false);
                this._setError(this._friendlyOpenError(err));
            }
        } finally {
            // Only clear the shared abort handle if this open() still owns it.
            if (this._bookFetchAbort === controller) {
                this._bookFetchAbort = null;
            }
        }
    }

    /**
     * Demo deep link (&book=<slug>&page=<n>): land on page n of actual PROSE.
     * "Page" = the reading-position location index (same unit as the footer's
     * "Page X of Y"), counted from the first real prose chapter — never the
     * cover, TOC or front matter, and never the saved last-location.
     */
    async _gotoProsePage(page, isStale) {
        if (!this.book || !this.rendition) return;
        const items = (this.book.spine && this.book.spine.spineItems) || [];
        if (!items.length) return;
        // Front-matter names to skip when hunting for the first prose chapter.
        const NONPROSE = /(^|[\/_-])(toc|nav|title|titlepage|cover|copyright|colophon|dedication|contents|halftitle|imprint|frontmatter|acknowledg\w*|epigraph)([\/_.-]|$)/i;
        let prose = null;
        for (const item of items) {
            if (isStale && isStale()) return;
            try {
                const href = String(item.href || "");
                if (NONPROSE.test(href)) continue;
                await item.load(this.book.load.bind(this.book));
                const doc = item.document;
                const text = (doc && doc.body)
                    ? (doc.body.textContent || "").replace(/\s+/g, " ").trim()
                    : "";
                item.unload();
                // Real prose runs long; TOC/half-title/dedication pages don't.
                if (text.length >= 900) { prose = item; break; }
            } catch (_) {}
        }
        if (!prose) prose = items[0];
        if (isStale && isStale()) return;
        await this.rendition.display(prose.href);
        const n = Math.max(1, page | 0);
        if (n > 1 && this.locationsReady) {
            try {
                if (isStale && isStale()) return;
                const loc = this.rendition.currentLocation();
                const startCfi = loc && loc.start && loc.start.cfi;
                const startLoc = (loc && loc.start && loc.start.location)
                    || (startCfi ? this.book.locations.locationFromCfi(startCfi) : 0)
                    || 0;
                const total = this.book.locations.length() || 0;
                const target = Math.min(startLoc + (n - 1), Math.max(0, total - 1));
                const cfi = this.book.locations.cfiFromLocation(target);
                if (cfi) await this.rendition.display(cfi);
            } catch (_) {}
        }
    }

    /**
     * Character-Cited Answers: open the reader on a cited passage.
     * Opens the given book, navigates to the cited chapter, locates the
     * snippet text inside it (epub.js section find → CFI) and applies a
     * temporary highlight via the annotations layer. Falls back gracefully:
     * chapter-only display when the snippet can't be located.
     */
    async openCitation(characterId, characterName, citation) {
        if (!citation || !citation.book_id) return;
        await this.open(characterId, characterName, citation.book_id);
        if (!this.book || !this.rendition || !this.isOpen) return;
        try {
            const href = String(citation.chapter_href || "").split("#")[0];
            let item = null;
            try { item = this.book.spine.get(href); } catch (_) {}
            if (!item && href) {
                const items = (this.book.spine && this.book.spine.spineItems) || [];
                item = items.find((it) =>
                    (it.href || "").split("#")[0].endsWith(href)) || null;
            }
            // epub.js find() is an exact substring match per text node, so
            // probe with progressively shorter heads of the snippet.
            const snippet = String(citation.snippet || "").replace(/\s+/g, " ").trim();
            const probes = [];
            if (snippet) {
                const words = snippet.split(" ");
                [12, 8, 5].forEach((n) => {
                    if (words.length >= n) probes.push(words.slice(0, n).join(" "));
                });
                if (!probes.length) probes.push(snippet);
            }
            let cfi = null;
            if (item) {
                await item.load(this.book.load.bind(this.book));
                for (const probe of probes) {
                    const found = item.find(probe) || [];
                    if (found.length) { cfi = found[0].cfi; break; }
                }
                item.unload();
            }
            if (cfi) {
                await this.rendition.display(cfi);
                try {
                    this.rendition.annotations.add(
                        "highlight", cfi, {}, null, "epubjs-hl",
                        { fill: "#f2c94c", "fill-opacity": "0.5", "mix-blend-mode": "multiply" }
                    );
                } catch (_) {}
            } else if (item) {
                await this.rendition.display(item.href);
            }
        } catch (e) {
            console.warn("[EpubReader] citation deep-link failed", e);
        }
    }

    /**
     * Fetch an EPUB into an ArrayBuffer with a hard timeout. Throws an
     * Error whose `.code` is a short tag we can map to a user message:
     *   - "abort"     : in-flight request was cancelled (new open() raced)
     *   - "timeout"   : fetch exceeded the timeout budget
     *   - "http-<n>"  : non-2xx HTTP response (e.g. http-404, http-403)
     *   - "network"   : transport-level failure (offline, DNS, CORS reject)
     *   - "empty"     : 0-byte response
     */
    async _fetchBookBuffer(url, controller) {
        const abort = controller || this._bookFetchAbort;
        const signal = abort ? abort.signal : undefined;
        const TIMEOUT_MS = 30000;
        const timer = setTimeout(() => {
            if (abort) {
                try { abort.abort("timeout"); } catch (_) {}
            }
        }, TIMEOUT_MS);
        let timedOut = false;
        const timeoutWatcher = setTimeout(() => { timedOut = true; }, TIMEOUT_MS);
        try {
            let res;
            try {
                res = await fetch(url, { signal, cache: "no-cache", credentials: "omit" });
            } catch (e) {
                if (timedOut || (signal && signal.aborted && String(signal.reason) === "timeout")) {
                    const err = new Error("timeout"); err.code = "timeout"; throw err;
                }
                if (e && e.name === "AbortError") {
                    const err = new Error("abort"); err.code = "abort"; throw err;
                }
                const err = new Error("network"); err.code = "network"; err.cause = e; throw err;
            }
            if (!res.ok) {
                const err = new Error(`http-${res.status}`);
                err.code = `http-${res.status}`;
                throw err;
            }
            const buf = await res.arrayBuffer();
            if (!buf || buf.byteLength === 0) {
                const err = new Error("empty"); err.code = "empty"; throw err;
            }
            return buf;
        } finally {
            clearTimeout(timer);
            clearTimeout(timeoutWatcher);
        }
    }

    /** Race a promise against a timeout. Rejects with `{code: tag}` on timeout. */
    _withTimeout(promise, ms, tag) {
        return new Promise((resolve, reject) => {
            const t = setTimeout(() => {
                const err = new Error(tag || "timeout");
                err.code = tag || "timeout";
                reject(err);
            }, ms);
            Promise.resolve(promise).then(
                (v) => { clearTimeout(t); resolve(v); },
                (e) => { clearTimeout(t); reject(e); }
            );
        });
    }

    /** Map an open() error into a short, user-friendly message. */
    _friendlyOpenError(err) {
        const code = (err && (err.code || err.message)) || "";
        if (code === "abort") {
            return ""; // user-initiated cancel: don't show an error
        }
        if (code === "timeout" || code === "display-timeout" || code === "ready-timeout") {
            return "This book is taking too long to load. Please check your connection and try again.";
        }
        if (typeof code === "string" && code.startsWith("http-")) {
            const status = code.slice(5);
            if (status === "404") return "This book file could not be found on the server.";
            if (status === "403" || status === "401") return "This book link has expired. Please close the reader and reopen it.";
            return `The server returned an error (${status}) while loading this book.`;
        }
        if (code === "network") {
            return "Could not download this book. Please check your internet connection.";
        }
        if (code === "empty") {
            return "This book file is empty or unavailable.";
        }
        if (code === "missing-url") {
            return "This book is missing a download link.";
        }
        // Anything else (epub.js parse error, malformed ZIP, etc.)
        return "This book could not be opened. The file may be missing or corrupted.";
    }

    close() {
        if (!this.isOpen) return;
        this.stopReadAloud();
        if (this._bookFetchAbort) {
            try { this._bookFetchAbort.abort(); } catch (_) {}
            this._bookFetchAbort = null;
        }
        // Invalidate any in-flight open(): its post-fetch stages (display /
        // ready / account state) check the generation and must not resurrect
        // a reader the user just closed — nor dispatch its bookReaderOpened
        // success signal (which would miscount the open funnel).
        this._openId = (this._openId || 0) + 1;
        this._setLoading(false);
        this.overlay.classList.remove("is-open");
        // Drop the mirrored page-color theme off the panel so a dark reading
        // session doesn't bleed its chrome colors into a later mockup-reader
        // open (which reuses #right-panel without re-running our theme mirror).
        if (this.embedded) {
            try {
                const rp = document.getElementById("right-panel");
                if (rp) delete rp.dataset.readerTheme;
            } catch (_) {}
        }
        this.isOpen = false;
        if (this._areaResizeObs) { try { this._areaResizeObs.disconnect(); } catch (_) {} this._areaResizeObs = null; }
        clearTimeout(this._areaResizeTimer);
        if (this._selPoll) { clearInterval(this._selPoll); this._selPoll = null; }
        this._polledSelText = "";
        this._resetBookWordCounts();
        if (this._playPoll) { clearInterval(this._playPoll); this._playPoll = null; }
        clearTimeout(this._clearCtxTimer);
        this._clearCtxTimer = setTimeout(() => {
            window.ActiveBookContext = null;
            window.dispatchEvent(new CustomEvent("bookContextChanged", { detail: null }));
        }, 90 * 1000);
        window.dispatchEvent(new CustomEvent("bookReaderClosed"));
    }

    // ───────────────────────────────────────────────────────────────────
    // Embedded mode — mount inside the lab right-panel reader view.
    // ───────────────────────────────────────────────────────────────────
    _mountOverlay() {
        // Prefer the lab right-panel reader view (embedded mode); fall back to
        // the legacy bio-panel; finally the document body.
        const readerView = document.querySelector('#right-panel .view-reader');
        if (readerView) {
            this.embedded = true;
            this._embedHost = readerView;
            if (this.overlay.parentNode !== readerView) readerView.appendChild(this.overlay);
            this.overlay.classList.add("er-embedded");
            this.overlay.classList.remove("in-bio-panel");
            return;
        }
        this.embedded = false;
        const bioPanel = document.getElementById("bio-panel");
        if (bioPanel && this.overlay.parentNode !== bioPanel) {
            bioPanel.appendChild(this.overlay);
        }
        this.overlay.classList.toggle("in-bio-panel", !!bioPanel);
        this.overlay.classList.remove("er-embedded");
    }

    _emitRelocated(location) {
        try {
            const cfi = location && location.start && location.start.cfi;
            const marks = this._readBookmarks();
            window.dispatchEvent(new CustomEvent("epubReaderRelocated", { detail: {
                chapter: this._lastChapter || "",
                pct: this._lastPct || 0,
                locText: this._lastLocText || "",
                bookmarked: !!(cfi && marks.some((m) => m.cfi === cfi)),
                atStart: !!(location && location.atStart),
                atEnd: !!(location && location.atEnd),
            }}));
        } catch (_) {}
    }

    // ───────────────────────────────────────────────────────────────────
    // Public API used by the lab's pretty panel-head toolbar (embedded mode).
    // Thin wrappers over the engine so index.html never touches private nodes.
    // ───────────────────────────────────────────────────────────────────
    goNext() {
        if (!this.rendition) return;
        return this.rendition.next();
    }
    goPrev() {
        if (!this.rendition) return;
        return this.rendition.prev();
    }
    goHref(href) { if (href && this.rendition) this.rendition.display(href); }
    goCfi(cfi) { if (cfi && this.rendition) return this.rendition.display(cfi); }
    /** Seek to a 0..1 fraction of the book (embedded progress-bar scrubber).
     *  Requires generated locations; no-ops gracefully until they're ready. */
    goPercentage(p) {
        if (!this.rendition || !this.book) return;
        const f = Math.max(0, Math.min(1, Number(p) || 0));
        if (!this.locationsReady || !this.book.locations || !this.book.locations.cfiFromPercentage) return;
        let cfi;
        try { cfi = this.book.locations.cfiFromPercentage(f); } catch (_) { cfi = null; }
        if (cfi) return this.rendition.display(cfi);
    }

    /** Flattened TOC with nesting depth for the pretty popover. */
    getToc() {
        const out = [];
        const walk = (items, depth) => (items || []).forEach((it) => {
            out.push({ label: it.label || "Untitled", href: it.href || "", depth });
            if (it.subitems && it.subitems.length) walk(it.subitems, depth + 1);
        });
        walk(this._navToc || [], 0);
        return out;
    }

    /** Whole-book search; returns [{cfi, excerpt, chapter}]. */
    async runSpineSearch(q) {
        q = (q || "").trim();
        if (!q || !this.book) return [];
        const results = [];
        try {
            const spineItems = (this.book.spine && this.book.spine.spineItems) || [];
            for (const item of spineItems) {
                try {
                    await item.load(this.book.load.bind(this.book));
                    const found = item.find(q) || [];
                    const href = (item.href || "").split("#")[0];
                    const chapter = (this._tocItems.find((it) => (it.href || "").split("#")[0] === href) || {}).label || "";
                    found.forEach((r) => results.push({ cfi: r.cfi, excerpt: (r.excerpt || "").trim(), chapter }));
                    item.unload();
                    if (results.length > 80) break;
                } catch (_) {}
            }
        } catch (_) {}
        return results;
    }

    getReaderTheme() { return this.prefs.theme; }
    setReaderTheme(t) {
        const allowed = ["parchment", "sepia", "quiet", "light", "dark"];
        this.prefs.theme = allowed.includes(t) ? t : "parchment";
        this._savePrefs();
        this._applyPrefsToRendition();
    }

    /** Font-size presets matching the mockup's s/m/l dots. */
    setFontSizeKey(key) {
        const map = { s: 95, m: 110, l: 135 };
        this.prefs.fontSize = map[key] || 110;
        this._savePrefs();
        this._applyPrefsToRendition();
    }

    setBrightness(v) {
        this.prefs.brightness = Math.max(0.4, Math.min(1.3, parseFloat(v) || 1.0));
        this._savePrefs();
        this._applyPrefsToRendition();
    }

    /** Line spacing preset — tight / normal / loose. */
    setLineHeight(v) {
        const allowed = [1.2, 1.5, 1.85];
        const n = parseFloat(v);
        this.prefs.lineHeight = allowed.includes(n) ? n : 1.5;
        this._savePrefs();
        this._applyPrefsToRendition();
    }

    /** Side-margin width preset (% of page) — narrow / normal / wide. */
    setMargin(v) {
        const allowed = [4, 8, 14];
        const n = parseInt(v, 10);
        this.prefs.marginPct = allowed.includes(n) ? n : 8;
        this._savePrefs();
        this._applyPrefsToRendition();
    }

    /** Text alignment — left | justify. */
    setJustify(v) {
        this.prefs.justify = v === "left" ? "left" : "justify";
        this._savePrefs();
        this._applyPrefsToRendition();
    }

    /** Font family — serif | sans | dyslexic (dyslexia-friendly). */
    setFontFamily(v) {
        const allowed = ["serif", "sans", "dyslexic"];
        this.prefs.fontFamily = allowed.includes(v) ? v : "serif";
        this._savePrefs();
        this._applyPrefsToRendition();
    }

    /** Page layout — auto (single/double per width) | single (force one). */
    async setColumns(v) {
        this.prefs.columns = v === "single" ? "single" : "auto";
        this._savePrefs();
        await this._reflowColumns();
    }

    /** Generic pref router used by the embedded "Aa" view popover so the bridge
     *  only needs one entry point. Returns true when handled. */
    setReaderPref(pref, val) {
        switch (pref) {
            case "lineHeight": this.setLineHeight(val); return true;
            case "marginPct":  this.setMargin(val);     return true;
            case "justify":    this.setJustify(val);    return true;
            case "fontFamily": this.setFontFamily(val); return true;
            case "columns":    this.setColumns(val);    return true;
            case "theme":      this.setReaderTheme(val); return true;
            default: return false;
        }
    }

    /** Current pref snapshot so the embedded popover can reflect saved state. */
    getReaderPrefs() { return { ...this.prefs }; }

    /** Switch between paged (page-turn) and scroll flow; re-renders at the
     *  current location. Falls back silently if the engine rejects the flow. */
    async setFlowMode(mode) {
        const flow = mode === "scroll" ? "scrolled-doc" : "paginated";
        this.prefs.flow = flow;
        this._savePrefs();
        if (!this.rendition || !this.book) return;
        const cur = this.rendition.currentLocation();
        const cfi = cur && cur.start && cur.start.cfi;
        try { this.rendition.destroy(); } catch (_) {}
        if (this._activeAnnotations) this._activeAnnotations.clear();
        try {
            this.rendition = this.book.renderTo(this.areaEl, {
                width: "100%",
                height: "100%",
                flow,
                manager: "default",
                allowScriptedContent: true,
                spread: this.prefs.columns === "single" ? "none" : "auto",
            });
            this._applyPrefsToRendition();
            this._registerContentHooks();
            this._wireRenditionEvents();
            await this.rendition.display(cfi || undefined);
            this._loadAndReplayAllHighlights();
        } catch (e) {
            console.warn("[EpubReader] flow switch failed", e);
        }
    }

    toggleBookmark() { this._toggleBookmarkAtCurrent(); return this.isBookmarkedNow(); }
    getBookmarks() { return this._readBookmarks(); }

    /** Export the active book's highlights + bookmarks as a Markdown file the
     *  reader can keep. Returns the number of items exported (0 = nothing to
     *  export, so the caller can show a hint instead of an empty download). */
    exportNotes() {
        const highlights = this._readHighlights();
        const bookmarks = this._readBookmarks();
        if (!highlights.length && !bookmarks.length) return 0;
        const meta = this.activeBookMeta || {};
        const title = meta.title || "Untitled";
        const author = meta.author || meta.characterName || "";
        const lines = [];
        lines.push(`# ${title}`);
        if (author) lines.push(`*by ${author}*`);
        lines.push("");
        lines.push(`_Notes exported ${new Date().toLocaleString()}_`);
        lines.push("");
        if (highlights.length) {
            lines.push(`## Highlights (${highlights.length})`);
            lines.push("");
            highlights.forEach((h) => {
                const text = (h.text || "").trim();
                if (text) lines.push(`> ${text.replace(/\n+/g, " ")}`);
                if (h.note) lines.push(`>`, `> **Note:** ${h.note.replace(/\n+/g, " ")}`);
                const loc = [h.chapter, h.savedAt].filter(Boolean).join(" · ");
                if (loc) lines.push(`>`, `> — ${loc}`);
                lines.push("");
            });
        }
        if (bookmarks.length) {
            lines.push(`## Bookmarks (${bookmarks.length})`);
            lines.push("");
            bookmarks.forEach((m) => {
                const label = m.label || "Bookmark";
                const when = m.savedAt ? ` _(${m.savedAt})_` : "";
                lines.push(`- **${label}**${when}`);
                if (m.snippet) lines.push(`  - ${m.snippet.replace(/\n+/g, " ")}`);
            });
            lines.push("");
        }
        const safeName = String(title).replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 80) || "notes";
        try {
            const blob = new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${safeName}-notes.md`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 4000);
        } catch (e) {
            console.warn("[EpubReader] export failed", e);
            return 0;
        }
        return highlights.length + bookmarks.length;
    }

    /** Read-aloud toggle for the embedded panel-head button. In embedded mode
     *  the engine's own `.er-play` button is hidden, so the pretty toolbar
     *  drives read-aloud through here. State sync flows back via the
     *  `epubReadAloudState` window event dispatched from `_setPlayUI`. */
    toggleReadAloud() { this._togglePlay(); return this.readAloudState(); }
    readAloudState() { return (this._readAloud && this._readAloud.state) || "idle"; }
    isReadAloudActive() { return this.readAloudState() === "playing"; }

    isBookmarkedNow() {
        if (!this.rendition) return false;
        const loc = this.rendition.currentLocation();
        const cfi = loc && loc.start && loc.start.cfi;
        return !!(cfi && this._readBookmarks().some((m) => m.cfi === cfi));
    }

    _buildOverlay() {
        const root = document.createElement("div");
        root.className = "epub-reader-overlay";
        root.dataset.theme = this.prefs.theme;
        root.innerHTML = `
            <div class="epub-reader-toolbar">
                <button class="er-toc-btn" title="Table of contents (T)"><i class="fas fa-list"></i></button>
                <button class="er-search-btn" title="Search (/)"><i class="fas fa-search"></i></button>
                <input type="search" class="er-search" placeholder="Search this book…" />
                <span class="er-title"></span>
                <button class="er-play" title="Read this page aloud" disabled>
                    <i class="fas fa-play"></i>
                </button>
                <button class="er-stop" title="Stop reading" disabled>
                    <i class="fas fa-stop"></i>
                </button>
                <button class="er-aa" title="Display settings"><span class="er-aa-label">Aa</span></button>
                <button class="er-bookmark" title="Toggle bookmark (B)"><i class="far fa-bookmark"></i></button>
                <button class="er-bookmarks-btn" title="Bookmarks &amp; highlights"><i class="fas fa-list-ul"></i></button>
                <button class="er-close" title="Close (Esc)"><i class="fas fa-times"></i></button>
            </div>

            <!-- Aa settings popover -->
            <div class="epub-reader-popover er-aa-popover" hidden>
                <div class="er-pop-row">
                    <label>Text size</label>
                    <div class="er-pop-controls">
                        <button data-act="font-minus">A−</button>
                        <span class="er-pop-value" data-show="fontSize">110%</span>
                        <button data-act="font-plus">A+</button>
                    </div>
                </div>
                <div class="er-pop-row">
                    <label>Font</label>
                    <div class="er-seg" data-pref="fontFamily">
                        <button data-val="serif">Serif</button>
                        <button data-val="sans">Sans</button>
                    </div>
                </div>
                <div class="er-pop-row">
                    <label>Theme</label>
                    <div class="er-seg" data-pref="theme">
                        <button data-val="light">Light</button>
                        <button data-val="sepia">Sepia</button>
                        <button data-val="dark">Dark</button>
                    </div>
                </div>
                <div class="er-pop-row">
                    <label>Line spacing</label>
                    <div class="er-seg" data-pref="lineHeight">
                        <button data-val="1.2">Tight</button>
                        <button data-val="1.5">Normal</button>
                        <button data-val="1.85">Loose</button>
                    </div>
                </div>
                <div class="er-pop-row">
                    <label>Margins</label>
                    <div class="er-seg" data-pref="marginPct">
                        <button data-val="4">Narrow</button>
                        <button data-val="8">Normal</button>
                        <button data-val="14">Wide</button>
                    </div>
                </div>
                <div class="er-pop-row">
                    <label>Alignment</label>
                    <div class="er-seg" data-pref="justify">
                        <button data-val="left">Left</button>
                        <button data-val="justify">Justify</button>
                    </div>
                </div>
                <div class="er-pop-row">
                    <label>Layout</label>
                    <div class="er-seg" data-pref="columns">
                        <button data-val="auto">Auto</button>
                        <button data-val="single">Single</button>
                    </div>
                </div>
                <div class="er-pop-row">
                    <label>Brightness</label>
                    <input type="range" class="er-brightness" min="40" max="100" step="5" />
                </div>
            </div>

            <!-- Bookmarks / highlights drawer -->
            <aside class="epub-reader-drawer er-marks-drawer" hidden>
                <div class="er-drawer-tabs">
                    <button class="is-active" data-tab="bookmarks">Bookmarks</button>
                    <button data-tab="highlights">Highlights</button>
                </div>
                <div class="er-drawer-body" data-tab-body="bookmarks"></div>
                <div class="er-drawer-body" data-tab-body="highlights" hidden></div>
            </aside>

            <div class="epub-reader-body">
                <aside class="epub-reader-toc"></aside>
                <div class="epub-reader-stage">
                    <button class="epub-reader-nav prev" title="Previous (←)" data-demo-id="reader-prev-page"><i class="fas fa-chevron-left"></i></button>
                    <div class="epub-reader-area"></div>
                    <button class="epub-reader-nav next" title="Next (→)" data-demo-id="reader-next-page"><i class="fas fa-chevron-right"></i></button>
                    <div class="epub-reader-loading" hidden></div>
                    <div class="epub-reader-error" hidden></div>
                    <div class="er-brightness-overlay"></div>
                    <!-- Sentence highlight overlay (rectangles inserted on demand) -->
                    <div class="er-sentence-overlay" aria-hidden="true"></div>
                    <!-- Current-word highlight overlay (read-aloud follow-along) -->
                    <div class="er-word-overlay" aria-hidden="true"></div>
                    <!-- Selection action bar: highlight colors + note/quote/copy -->
                    <div class="er-selection-bar" role="toolbar" aria-label="Selection actions" hidden>
                        <div class="er-sel-row">
                            ${HIGHLIGHT_COLORS.map(c => `<button class="sel-color ${c.cls}" data-color="${c.id}" data-tip="${c.tip}" aria-label="${c.tip}"></button>`).join("")}
                        </div>
                        <div class="er-sel-divider"></div>
                        <div class="er-sel-row">
                            <button class="sel-action er-hl-ask" data-act="ask" data-tip="Ask the avatar" aria-label="Ask the avatar about this passage"><i class="fas fa-comment-dots"></i></button>
                            <button class="sel-action er-hl-note" data-act="note" data-tip="Add a note" aria-label="Add note"><i class="fas fa-sticky-note"></i></button>
                            <button class="sel-action er-hl-quote" data-act="quote" data-tip="Copy quote" aria-label="Copy quote"><i class="fas fa-quote-right"></i></button>
                            <button class="sel-action er-hl-copy" data-act="copy" data-tip="Copy passage" aria-label="Copy text"><i class="fas fa-copy"></i></button>
                        </div>
                        <div class="er-sel-arrow" aria-hidden="true"></div>
                    </div>
                    <!-- Inline note editor (replaces window.prompt) -->
                    <div class="er-note-popover" hidden>
                        <textarea class="er-note-input" placeholder="A small note for yourself…" rows="2"></textarea>
                        <div class="er-note-actions">
                            <button class="er-note-cancel">Cancel</button>
                            <button class="er-note-save">Save</button>
                        </div>
                    </div>
                    <!-- Copy / quote confirmation toast -->
                    <div class="er-reader-toast" aria-live="polite"><em>Copied</em></div>
                    <!-- Dictionary popover -->
                    <div class="er-dict-popover" hidden></div>
                </div>
            </div>

            <div class="epub-reader-footer">
                <span class="er-chapter">—</span>
                <div class="er-scrubber-wrap">
                    <input class="er-scrubber" type="range" min="0" max="1000" step="1" value="0" />
                    <div class="er-scrubber-tip" hidden></div>
                </div>
                <span class="er-time-left">—</span>
                <span class="er-loc">—</span>
            </div>
        `;
        document.body.appendChild(root);

        // Cache nodes
        this.overlay         = root;
        this.toolbarEl       = root.querySelector(".epub-reader-toolbar");
        this.titleEl         = root.querySelector(".er-title");
        this.searchInput     = root.querySelector(".er-search");
        this.tocEl           = root.querySelector(".epub-reader-toc");
        this.areaEl          = root.querySelector(".epub-reader-area");
        this.loadingEl       = root.querySelector(".epub-reader-loading");
        this.errorEl         = root.querySelector(".epub-reader-error");
        this.chapterEl       = root.querySelector(".er-chapter");
        this.locEl           = root.querySelector(".er-loc");
        this.timeLeftEl      = root.querySelector(".er-time-left");
        this.scrubberEl      = root.querySelector(".er-scrubber");
        this.tipEl           = root.querySelector(".er-scrubber-tip");
        this.bookmarkBtn     = root.querySelector(".er-bookmark");
        this.playBtn         = root.querySelector(".er-play");
        this.stopBtn         = root.querySelector(".er-stop");
        this.aaBtn           = root.querySelector(".er-aa");
        this.aaPopoverEl     = root.querySelector(".er-aa-popover");
        this.brightnessOverlay = root.querySelector(".er-brightness-overlay");
        this.brightnessRange = root.querySelector(".er-brightness");
        this.sentenceOverlay = root.querySelector(".er-sentence-overlay");
        this.wordOverlay     = root.querySelector(".er-word-overlay");
        this.selectionBar    = root.querySelector(".er-selection-bar");
        this.notePopover     = root.querySelector(".er-note-popover");
        this.noteInput       = root.querySelector(".er-note-input");
        this.toastEl         = root.querySelector(".er-reader-toast");
        this.dictPopover     = root.querySelector(".er-dict-popover");
        this.marksDrawer     = root.querySelector(".er-marks-drawer");
        this.stageEl         = root.querySelector(".epub-reader-stage");

        // Toolbar wiring
        root.querySelector(".er-close").addEventListener("click", () => this.close());
        root.querySelector(".er-toc-btn").addEventListener("click", () => this._toggleToc());
        root.querySelector(".er-search-btn").addEventListener("click", () => this._toggleSearch());
        root.querySelector(".er-bookmarks-btn").addEventListener("click", () => this._toggleMarksDrawer());
        root.querySelector(".er-bookmark").addEventListener("click", () => this._toggleBookmarkAtCurrent());
        root.querySelector(".epub-reader-nav.prev").addEventListener("click", () => this.rendition && this.rendition.prev());
        root.querySelector(".epub-reader-nav.next").addEventListener("click", () => this.rendition && this.rendition.next());

        // Aa popover
        this.aaBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            this._toggleAaPopover();
        });
        this.aaPopoverEl.addEventListener("click", (e) => e.stopPropagation());
        document.addEventListener("click", (e) => {
            if (!this.aaPopoverEl.hidden && !this.aaPopoverEl.contains(e.target) && e.target !== this.aaBtn) {
                this.aaPopoverEl.hidden = true;
            }
        });
        this._wireAaControls();

        // Play button
        this.playBtn.addEventListener("click", () => this._togglePlay());
        if (this.stopBtn) this.stopBtn.addEventListener("click", () => this.stopReadAloud());

        // Search input
        let searchTimer = null;
        this.searchInput.addEventListener("input", (e) => {
            clearTimeout(searchTimer);
            const q = e.target.value.trim();
            searchTimer = setTimeout(() => this._runSearch(q), 240);
        });

        // Scrubber
        this.scrubberEl.addEventListener("input", () => this._scrubberPreview());
        this.scrubberEl.addEventListener("change", () => this._scrubberCommit());

        // Marks drawer tabs
        this.marksDrawer.querySelectorAll(".er-drawer-tabs button").forEach(btn => {
            btn.addEventListener("click", () => this._switchMarksTab(btn.dataset.tab));
        });

        // Selection bar — color swatches highlight; actions note/quote/copy.
        // stopPropagation so the mouseup that lands on a menu button doesn't
        // re-trigger the iframe selection logic underneath it.
        this.selectionBar.querySelectorAll(".sel-color").forEach(btn => {
            btn.addEventListener("mousedown", (e) => e.preventDefault());
            btn.addEventListener("click", (e) => { e.stopPropagation(); this._addHighlight(btn.dataset.color); });
        });
        this.selectionBar.querySelectorAll(".sel-action").forEach(btn => {
            btn.addEventListener("mousedown", (e) => e.preventDefault());
            btn.addEventListener("click", (e) => {
                e.stopPropagation();
                const act = btn.dataset.act;
                if (act === "ask")   this._askAvatarAboutSelection(btn);
                else if (act === "note")  this._addHighlight(HIGHLIGHT_COLORS[0].id, { withNote: true });
                else if (act === "quote") this._quoteSelection(btn);
                else if (act === "copy")  this._copySelection(btn);
            });
        });

        // Note editor popover actions
        this.notePopover.querySelector(".er-note-cancel").addEventListener("click", () => this._closeNoteEditor());
        this.notePopover.querySelector(".er-note-save").addEventListener("click", () => this._saveNote());

        // Touch swipe on stage
        let tx = 0, ty = 0;
        this.stageEl.addEventListener("touchstart", (e) => {
            const t = e.changedTouches[0]; tx = t.clientX; ty = t.clientY;
        }, { passive: true });
        this.stageEl.addEventListener("touchend", (e) => {
            const t = e.changedTouches[0];
            const dx = t.clientX - tx, dy = t.clientY - ty;
            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) && this.rendition) {
                if (dx < 0) this.rendition.next(); else this.rendition.prev();
            }
        }, { passive: true });

        window.addEventListener("resize", () => this._refreshSentenceHighlightPosition());
    }

    _wireGlobalKeys() {
        document.addEventListener("keydown", (e) => {
            if (!this.isOpen) return;
            if (e.target === this.searchInput && e.key !== "Escape") return;

            if (e.key === "Escape") {
                if (!this.aaPopoverEl.hidden) { this.aaPopoverEl.hidden = true; return; }
                if (!this.dictPopover.hidden) { this.dictPopover.hidden = true; return; }
                this.close();
                return;
            }
            if (e.key === "ArrowLeft" && this.rendition)  { this.rendition.prev(); e.preventDefault(); }
            else if (e.key === "ArrowRight" && this.rendition) { this.rendition.next(); e.preventDefault(); }
            else if (e.key === "/")                        { this._toggleSearch(true); e.preventDefault(); }
            else if (e.key.toLowerCase() === "t")          { this._toggleToc(); }
            else if (e.key.toLowerCase() === "b")          { this._toggleBookmarkAtCurrent(); }
            else if (e.key === " " && e.target === document.body) {
                this._togglePlay(); e.preventDefault();
            }
        });
    }

    _loadPrefs() {
        try {
            const raw = localStorage.getItem(LS.prefs());
            if (raw) return Object.assign({}, DEFAULT_PREFS, JSON.parse(raw));
        } catch (_) {}
        return { ...DEFAULT_PREFS };
    }
    _savePrefs() {
        try { localStorage.setItem(LS.prefs(), JSON.stringify(this.prefs)); } catch (_) {}
    }
    _applyPrefsToRendition() {
        if (!this.rendition) return;
        // Dyslexia-friendly stack favours heavier, more-distinct letterforms
        // (Comic Sans MS / Verdana / Tahoma are the widely-available faces that
        // dyslexia readability guidance recommends when a dedicated font like
        // OpenDyslexic isn't installed).
        const famMap = {
            sans:     `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`,
            dyslexic: `"OpenDyslexic", "Comic Sans MS", "Comic Neue", Verdana, Tahoma, sans-serif`,
            serif:    `Georgia, "Times New Roman", serif`,
        };
        const fam = famMap[this.prefs.fontFamily] || famMap.serif;
        const themes = {
            light:     { body: { color: "#1d1d1f", background: "#ffffff" } },
            sepia:     { body: { color: "#4a3b28", background: "#efe0c4" } },
            parchment: { body: { color: "#3a2f1d", background: "#f5efe2" } },
            quiet:     { body: { color: "#cdbfa8", background: "#211b14" } },
            dark:      { body: { color: "#e8e8e8", background: "#1a1a1a" } },
        };
        try {
            this.rendition.themes.register("light", themes.light);
            this.rendition.themes.register("sepia", themes.sepia);
            this.rendition.themes.register("parchment", themes.parchment);
            this.rendition.themes.register("quiet", themes.quiet);
            this.rendition.themes.register("dark",  themes.dark);
            this.rendition.themes.select(this.prefs.theme);
            this.rendition.themes.fontSize(`${this.prefs.fontSize}%`);
            this.rendition.themes.font(fam);
            this.rendition.themes.override("line-height", String(this.prefs.lineHeight));
            this.rendition.themes.override("text-align", this.prefs.justify);
            // Force the page background/text to the theme with !important so a
            // publisher's own stylesheet can't leave a white page sitting inside
            // the themed frame (the "different-colored square" around the book).
            const sel = themes[this.prefs.theme] || themes.parchment;
            this.rendition.themes.override("background-color", sel.body.background, true);
            this.rendition.themes.override("color", sel.body.color, true);
        } catch (e) { console.warn("[EpubReader] theme apply failed", e); }

        const pad = `${Math.max(2, Math.min(20, this.prefs.marginPct))}%`;
        this.areaEl.style.padding = `20px ${pad}`;

        const a = Math.max(0, Math.min(0.6, 1 - this.prefs.brightness));
        this.brightnessOverlay.style.opacity = this.embedded ? "0" : String(a);
        if (this.embedded && this.areaEl) {
            const b = this.prefs.brightness;
            this.areaEl.style.filter = (b && b !== 1) ? `brightness(${b})` : "";
        }

        this.overlay.dataset.theme = this.prefs.theme;
        // Mirror the theme onto the lab's right-panel so the pretty chrome
        // (footer + surrounds) follows the page color instead of only the book
        // iframe changing color. Embedded mode only.
        if (this.embedded) {
            try {
                const rp = document.getElementById("right-panel");
                if (rp) rp.dataset.readerTheme = this.prefs.theme;
            } catch (_) {}
        }
        this.brightnessRange.value = String(Math.round(this.prefs.brightness * 100));
        this._syncAaPopoverState();
    }
    _wireAaControls() {
        this.aaPopoverEl.querySelector('[data-act="font-plus"]').addEventListener("click", () => this._adjustFont(+10));
        this.aaPopoverEl.querySelector('[data-act="font-minus"]').addEventListener("click", () => this._adjustFont(-10));
        this.aaPopoverEl.querySelectorAll(".er-seg").forEach(seg => {
            const pref = seg.dataset.pref;
            seg.querySelectorAll("button").forEach(btn => {
                btn.addEventListener("click", () => {
                    const raw = btn.dataset.val;
                    const val = (pref === "lineHeight" || pref === "marginPct") ? parseFloat(raw) : raw;
                    this.prefs[pref] = val;
                    this._savePrefs();
                    if (pref === "columns") {
                        this._reflowColumns();
                    } else {
                        this._applyPrefsToRendition();
                    }
                });
            });
        });
        this.brightnessRange.addEventListener("input", (e) => {
            this.prefs.brightness = Math.max(0.4, Math.min(1.0, parseInt(e.target.value, 10) / 100));
            this._savePrefs(); this._applyPrefsToRendition();
        });
    }
    _syncAaPopoverState() {
        const valEl = this.aaPopoverEl.querySelector('[data-show="fontSize"]');
        if (valEl) valEl.textContent = `${this.prefs.fontSize}%`;
        this.aaPopoverEl.querySelectorAll(".er-seg").forEach(seg => {
            const pref = seg.dataset.pref;
            const cur = String(this.prefs[pref]);
            seg.querySelectorAll("button").forEach(btn => {
                btn.classList.toggle("is-active", btn.dataset.val === cur);
            });
        });
    }
    _toggleAaPopover() {
        const wasHidden = this.aaPopoverEl.hidden;
        this.aaPopoverEl.hidden = !wasHidden;
        if (wasHidden) {
            // Anchor below the Aa button
            const r = this.aaBtn.getBoundingClientRect();
            const ovr = this.overlay.getBoundingClientRect();
            this.aaPopoverEl.style.top = `${r.bottom - ovr.top + 6}px`;
            this.aaPopoverEl.style.right = `${ovr.right - r.right}px`;
            this._syncAaPopoverState();
        }
    }
    _adjustFont(delta) {
        this.prefs.fontSize = Math.max(70, Math.min(180, this.prefs.fontSize + delta));
        this._savePrefs(); this._applyPrefsToRendition();
    }
    async _reflowColumns() {
        if (!this.rendition || !this.book) return;
        const cur = this.rendition.currentLocation();
        const cfi = cur && cur.start && cur.start.cfi;
        try { this.rendition.destroy(); } catch (_) {}
        if (this._activeAnnotations) this._activeAnnotations.clear();
        this.rendition = this.book.renderTo(this.areaEl, {
            width: "100%",
            height: "100%",
            flow: this.prefs.flow || "paginated",
            manager: "default",
            allowScriptedContent: true,
            spread: this.prefs.columns === "single" ? "none" : "auto",
        });
        this._applyPrefsToRendition();
        this._registerContentHooks();
        this._wireRenditionEvents();
        await this.rendition.display(cfi || undefined);
        this._loadAndReplayAllHighlights();
    }

    /**
     * Wire the relocated / selected / rendered listeners on the current
     * rendition. Centralised so _reflowColumns can call this after
     * destroying and re-creating the rendition without leaking the prior
     * listeners (the destroyed rendition's event emitter goes with it).
     */
    _wireRenditionEvents() {
        if (!this.rendition) return;
        this.rendition.on("relocated", (location) => this._onRelocated(location));
        this.rendition.on("selected", (cfiRange, contents) => this._onSelection(cfiRange, contents));
        this.rendition.on("rendered", (section) => {
            this._replayHighlightsForSection(section);
            this._refreshSentenceHighlightPosition();
        });
    }

    _registerContentHooks() {
        if (!this.rendition || !this.rendition.hooks || !this.rendition.hooks.content) return;
        this.rendition.hooks.content.register((contents) => {
            try {
                const doc = contents.document;
                if (!doc) return;
                const style = doc.createElement("style");
                style.textContent = `
                    .epubjs-hl { transition: opacity .15s; }
                    .epubjs-hl:hover { opacity: 0.7; cursor: pointer; }
                    body { -webkit-user-select: text; user-select: text; }
                    /* Make body paragraphs follow the reader's font-size control.
                       Many publisher EPUBs hard-code px on <p>, which defeats
                       epub.js themes.fontSize() (it scales <body> only). Pinning
                       paragraphs to 1em re-couples them to the scaled body size so
                       Smaller/Bigger actually works on those books. */
                    p { font-size: 1em !important; }
                `;
                doc.head && doc.head.appendChild(style);

                const lookupAt = (e) => {
                    const sel = doc.getSelection && doc.getSelection();
                    let word = "";
                    if (sel && sel.toString().trim()) {
                        word = sel.toString().trim().split(/\s+/)[0];
                    } else if (e.target && e.target.textContent) {
                        const w = this._wordAtPoint(doc, e.clientX, e.clientY);
                        if (w) word = w;
                    }
                    if (word) this._lookupWord(word, e);
                };
                doc.addEventListener("dblclick", lookupAt);

                let pressTimer = null, pressStart = null, moved = false;
                const startPress = (e) => {
                    moved = false;
                    pressStart = { x: e.clientX || (e.touches && e.touches[0].clientX),
                                   y: e.clientY || (e.touches && e.touches[0].clientY) };
                    clearTimeout(pressTimer);
                    pressTimer = setTimeout(() => {
                        if (moved) return;
                        const evt = { clientX: pressStart.x, clientY: pressStart.y, target: e.target };
                        lookupAt(evt);
                    }, 550);
                };
                const movePress = (e) => {
                    if (!pressStart) return;
                    const x = e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX);
                    const y = e.clientY || (e.touches && e.touches[0] && e.touches[0].clientY);
                    if (Math.abs((x || 0) - pressStart.x) > 8 || Math.abs((y || 0) - pressStart.y) > 8) {
                        moved = true; clearTimeout(pressTimer);
                    }
                };
                const endPress = () => { clearTimeout(pressTimer); pressStart = null; };
                doc.addEventListener("touchstart", startPress, { passive: true });
                doc.addEventListener("touchmove",  movePress,  { passive: true });
                doc.addEventListener("touchend",   endPress);
                doc.addEventListener("mousedown",  startPress);
                doc.addEventListener("mousemove",  movePress);
                doc.addEventListener("mouseup",    endPress);

                // ── Selection-bar fallback for WebKit (Safari / iOS) ──
                // CRITICAL: the rendition is created with allowScriptedContent:true
                // so the iframe sandbox includes `allow-scripts`. With it OFF,
                // WebKit accepts parent-side DOM edits (our font-size style here
                // works) but will NOT invoke ANY event handler in the iframe —
                // so epub.js's native `selected` event AND these mouseup/
                // contextmenu fallbacks all stayed dead, leaving the OS callout
                // and never our highlight / Ask-the-avatar bar.
                // These handlers detect the selection directly on pointer/touch
                // release and route it through the same path. Idempotent with
                // epub.js's native event. (A 250ms poll in _ensureSelectionPoll
                // is the final safety net for any browser that still drops them.)
                const detectSelection = () => setTimeout(() => this._detectSelectionInContents(contents), 60);
                doc.addEventListener("mouseup",   detectSelection);
                doc.addEventListener("touchend",  detectSelection);
                doc.addEventListener("pointerup", detectSelection);

                // WebKit shows the OS callout / right-click menu on a selection and
                // never surfaces our custom bar. When text is selected, suppress the
                // native menu and show the highlight / Ask-the-avatar bar instead.
                doc.addEventListener("contextmenu", (e) => {
                    try {
                        const s = doc.getSelection && doc.getSelection();
                        if (s && !s.isCollapsed && s.rangeCount && s.toString().trim()) {
                            e.preventDefault();
                            this._detectSelectionInContents(contents);
                        }
                    } catch (_) {}
                });
            } catch (err) {
                console.warn("[EpubReader] content hook failed", err);
            }
        });
    }

    /** Read the live selection out of an iframe's `contents`, build a CFI when
     *  possible and surface the selection bar — or dismiss it when the
     *  selection has collapsed. Shared by the in-iframe and parent-document
     *  release handlers so both paths behave identically. */
    _detectSelectionInContents(contents) {
        let doc = null;
        try { doc = contents && contents.document; } catch (_) {}
        if (!doc) return;
        try {
            const s = doc.getSelection && doc.getSelection();
            const hasText = s && !s.isCollapsed && s.rangeCount && s.toString().trim();
            if (!hasText) {
                if (this._lastSelection) this._onSelection(null, contents);
                return;
            }
            let cfi = null;
            try {
                const base = contents.cfiBase || (contents.section && contents.section.cfiBase);
                if (window.ePub && window.ePub.CFI && base) {
                    cfi = new window.ePub.CFI(s.getRangeAt(0), base).toString();
                }
            } catch (_) {}
            if (cfi) {
                this._onSelection(cfi, contents);
            } else {
                // Couldn't build a CFI — still surface the bar, but keep any
                // cfiRange epub.js already set so highlighting still works.
                this._lastSelection = this._lastSelection || {};
                this._lastSelection.contents = contents;
                this._lastSelection.text = s.toString().trim();
                this._showSelectionBar(s, contents);
                this._publishContext({ initial: false });
            }
        } catch (_) {}
    }

    /** A drag-selection in the narrow embedded panel routinely ends with the
     *  pointer OUTSIDE the iframe (over the page margin or panel chrome), so the
     *  iframe never receives mouseup and the bar never showed. Watch releases on
     *  the parent document too and check every rendition iframe for a live
     *  selection. Bound once. */
    _ensureParentSelectionWatch() {
        if (this._parentSelBound) return;
        this._parentSelBound = true;
        const onParentRelease = () => setTimeout(() => {
            if (!this.isOpen || !this.rendition) return;
            let all = [];
            try { all = this.rendition.getContents() || []; } catch (_) { return; }
            let withSel = null;
            for (const c of all) {
                try {
                    const s = c && c.document && c.document.getSelection && c.document.getSelection();
                    if (s && !s.isCollapsed && s.rangeCount && s.toString().trim()) { withSel = c; break; }
                } catch (_) {}
            }
            if (withSel) this._detectSelectionInContents(withSel);
            else if (this._lastSelection) { this._lastSelection = null; this._hideSelectionBar(); }
        }, 60);
        document.addEventListener("mouseup", onParentRelease);
        document.addEventListener("pointerup", onParentRelease);
    }

    /** epub.js's default manager only re-lays-out on a window resize event. The
     *  reading panel resizes via a CSS variable (drag handle / setPanelWidth /
     *  open transition) with no window resize, so the column geometry goes stale
     *  → overlapping/clipped text. Re-measure (debounced) whenever the reading
     *  area changes size. */
    _ensureAreaResizeObserver() {
        if (this._areaResizeObs || typeof ResizeObserver === "undefined" || !this.areaEl) return;
        this._areaResizeObs = new ResizeObserver(() => {
            clearTimeout(this._areaResizeTimer);
            this._areaResizeTimer = setTimeout(() => {
                if (!this.rendition || !this.isOpen) return;
                if (!this.areaEl.clientWidth || !this.areaEl.clientHeight) return;
                try { this.rendition.resize(); } catch (_) {}
            }, 160);
        });
        try { this._areaResizeObs.observe(this.areaEl); } catch (_) {}
    }

    /** Final safety net for selection detection. Event delivery into the
     *  reading iframe is unreliable across browser/sandbox combinations, so
     *  poll the live selection out of every rendition iframe and surface /
     *  dismiss the bar whenever the selected text changes. Cheap and only acts
     *  on an actual change, so it never fights the user mid-drag. */
    _ensureSelectionPoll() {
        if (this._selPoll) return;
        this._polledSelText = "";
        this._selPoll = setInterval(() => {
            if (!this.isOpen || !this.rendition) return;
            let all = [];
            try { all = this.rendition.getContents() || []; } catch (_) { return; }
            let withSel = null, text = "";
            for (const c of all) {
                try {
                    const s = c && c.document && c.document.getSelection && c.document.getSelection();
                    if (s && !s.isCollapsed && s.rangeCount && s.toString().trim()) {
                        withSel = c; text = s.toString().trim(); break;
                    }
                } catch (_) {}
            }
            if (text && text !== this._polledSelText) {
                this._polledSelText = text;
                this._detectSelectionInContents(withSel);
            } else if (!text && this._polledSelText) {
                this._polledSelText = "";
                if (this._lastSelection) { this._lastSelection = null; this._hideSelectionBar(); }
            }
        }, 250);
    }

    _wordAtPoint(doc, x, y) {
        try {
            let range;
            if (doc.caretRangeFromPoint) range = doc.caretRangeFromPoint(x, y);
            else if (doc.caretPositionFromPoint) {
                const pos = doc.caretPositionFromPoint(x, y);
                if (pos) {
                    range = doc.createRange();
                    range.setStart(pos.offsetNode, pos.offset);
                    range.collapse(true);
                }
            }
            if (!range) return "";
            const node = range.startContainer;
            if (node.nodeType !== 3) return "";
            const text = node.textContent || "";
            let s = range.startOffset, e = range.startOffset;
            const isWord = (c) => /[\p{L}\p{N}'-]/u.test(c);
            while (s > 0 && isWord(text[s - 1])) s--;
            while (e < text.length && isWord(text[e])) e++;
            return text.slice(s, e).trim();
        } catch (_) { return ""; }
    }

    _toggleToc(forceOpen) {
        const open = forceOpen != null ? forceOpen : !this.tocEl.classList.contains("is-open");
        this.tocEl.classList.toggle("is-open", open);
    }
    _toggleSearch(forceOpen) {
        const open = forceOpen != null ? forceOpen : !this.toolbarEl.classList.contains("searching");
        this.toolbarEl.classList.toggle("searching", open);
        if (open) setTimeout(() => this.searchInput.focus(), 0);
    }
    _renderToc(nav) {
        if (!nav || !nav.toc) return;
        this._navToc = nav.toc;
        this._tocItems = [];
        this._tocByHref = {};
        const html = (items, depth = 0) => items.map((it) => {
            this._tocItems.push(it);
            const baseHref = (it.href || "").split("#")[0];
            if (baseHref) this._tocByHref[baseHref] = it.label || "Untitled";
            return `
                <div class="toc-item ${depth ? "toc-sub" : ""}" data-href="${this._escape(it.href)}">${this._escape(it.label || "Untitled")}</div>
                ${it.subitems && it.subitems.length ? html(it.subitems, depth + 1) : ""}
            `;
        }).join("");
        this.tocEl.innerHTML = html(nav.toc);
        this.tocEl.querySelectorAll(".toc-item").forEach((el) => {
            el.addEventListener("click", () => {
                const href = el.getAttribute("data-href");
                if (href && this.rendition) this.rendition.display(href);
            });
        });
    }

    async _runSearch(q) {
        if (!q || !this.book) {
            try { this._renderToc(await this.book.loaded.navigation); } catch (_) {}
            return;
        }
        this.tocEl.classList.add("is-open");
        this.tocEl.innerHTML = `<div class="toc-search-status">Searching “${this._escape(q)}”…</div>`;
        try {
            const spineItems = this.book.spine && this.book.spine.spineItems ? this.book.spine.spineItems : [];
            const results = [];
            for (const item of spineItems) {
                try {
                    await item.load(this.book.load.bind(this.book));
                    const found = item.find(q) || [];
                    const href = (item.href || "").split("#")[0];
                    const chapter = (this._tocItems.find(it => (it.href || "").split("#")[0] === href) || {}).label || "";
                    found.forEach((r) => results.push({ ...r, chapter }));
                    item.unload();
                    if (results.length > 80) break;
                } catch (_) {}
            }
            const html = results.length
                ? results.map((r) => `
                    <div class="toc-item toc-search-hit" data-cfi="${this._escape(r.cfi)}">
                        <div class="toc-search-chapter">${this._escape(r.chapter || "—")}</div>
                        <div class="toc-search-snippet">${this._highlightQuery(this._escape((r.excerpt || "").trim().slice(0, 160)), q)}</div>
                    </div>`).join("")
                : `<div class="toc-item">No matches.</div>`;
            this.tocEl.innerHTML = `
                <div class="toc-search-header">
                    <span>Search: “${this._escape(q)}”</span>
                    <small>${results.length} match${results.length === 1 ? "" : "es"}</small>
                </div>${html}`;
            this.tocEl.querySelectorAll(".toc-item[data-cfi]").forEach((el) => {
                el.addEventListener("click", () => {
                    const cfi = el.getAttribute("data-cfi");
                    if (!cfi || !this.rendition) return;
                    this.rendition.display(cfi).then(() => this._flashSearchHit(cfi)).catch(() => {});
                });
            });
        } catch (e) {
            console.warn("[EpubReader] search failed", e);
            this.tocEl.innerHTML = `<div class="toc-item">Search failed.</div>`;
        }
    }
    _highlightQuery(escapedHtml, q) {
        if (!q) return escapedHtml;
        const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
        return escapedHtml.replace(re, "<mark>$1</mark>");
    }

    _toggleMarksDrawer(forceOpen) {
        const open = forceOpen != null ? forceOpen : this.marksDrawer.hidden;
        this.marksDrawer.hidden = !open;
        if (open) this._renderMarksDrawer();
    }
    _switchMarksTab(tab) {
        this.marksDrawer.querySelectorAll(".er-drawer-tabs button").forEach(b => {
            b.classList.toggle("is-active", b.dataset.tab === tab);
        });
        this.marksDrawer.querySelectorAll(".er-drawer-body").forEach(b => {
            b.hidden = b.dataset.tabBody !== tab;
        });
        this._renderMarksDrawer();
    }
    _renderMarksDrawer() {
        const bmBody = this.marksDrawer.querySelector('[data-tab-body="bookmarks"]');
        const hlBody = this.marksDrawer.querySelector('[data-tab-body="highlights"]');

        const bookmarks = this._readBookmarks();
        bmBody.innerHTML = bookmarks.length ? bookmarks.map((m, i) => `
            <div class="er-mark-item" data-cfi="${this._escape(m.cfi)}" data-idx="${i}">
                <div class="er-mark-main">
                    <div class="er-mark-label"><i class="fas fa-bookmark"></i> ${this._escape(m.label || "Bookmark")}</div>
                    ${m.snippet ? `<div class="er-mark-snippet">${this._escape(m.snippet)}</div>` : ""}
                    <div class="er-mark-meta">${this._escape(m.savedAt || "")}</div>
                </div>
                <button class="er-mark-del" title="Remove"><i class="fas fa-times"></i></button>
            </div>`).join("") : `<div class="er-mark-empty">No bookmarks yet. Press B or the bookmark button to save the current page.</div>`;

        const highlights = this._readHighlights();
        hlBody.innerHTML = highlights.length ? highlights.map((h, i) => {
            const swatch = HIGHLIGHT_COLORS.find(c => c.id === h.color) || HIGHLIGHT_COLORS[0];
            return `
                <div class="er-mark-item" data-cfi="${this._escape(h.cfiRange)}" data-idx="${i}">
                    <div class="er-mark-main">
                        <div class="er-mark-label">
                            <span class="er-mark-swatch" style="background:${swatch.color}"></span>
                            ${this._escape(h.text || "")}
                        </div>
                        ${h.note ? `<div class="er-mark-note">${this._escape(h.note)}</div>` : ""}
                        <div class="er-mark-meta">${this._escape(h.chapter || "")} · ${this._escape(h.savedAt || "")}</div>
                    </div>
                    <button class="er-mark-del" title="Remove"><i class="fas fa-times"></i></button>
                </div>`;
        }).join("") : `<div class="er-mark-empty">No highlights yet. Select text in the book to add a highlight.</div>`;

        // Wire jump + delete
        this.marksDrawer.querySelectorAll(".er-mark-item").forEach(el => {
            el.querySelector(".er-mark-main").addEventListener("click", () => {
                if (this.rendition) this.rendition.display(el.dataset.cfi);
            });
            el.querySelector(".er-mark-del").addEventListener("click", (e) => {
                e.stopPropagation();
                const idx = parseInt(el.dataset.idx, 10);
                const isHL = el.parentElement.dataset.tabBody === "highlights";
                if (isHL) this._removeHighlightByIndex(idx);
                else this._removeBookmarkByIndex(idx);
                this._renderMarksDrawer();
            });
        });
    }
    _readBookmarks() {
        if (!this.activeCharacterId) return [];
        try { return JSON.parse(localStorage.getItem(LS.marks(this.activeCharacterId, this.activeBookKey)) || "[]"); }
        catch (_) { return []; }
    }
    _writeBookmarks(arr) {
        if (!this.activeCharacterId) return;
        try { localStorage.setItem(LS.marks(this.activeCharacterId, this.activeBookKey), JSON.stringify(arr)); } catch (_) {}
        this._saveReadingStateToAccount();
    }
    _toggleBookmarkAtCurrent() {
        if (!this.rendition) return;
        const loc = this.rendition.currentLocation();
        const cfi = loc && loc.start && loc.start.cfi;
        if (!cfi) return;
        const marks = this._readBookmarks();
        const idx = marks.findIndex((m) => m.cfi === cfi);
        if (idx >= 0) {
            marks.splice(idx, 1);
            this.bookmarkBtn.innerHTML = `<i class="far fa-bookmark"></i>`;
        } else {
            const snippet = (this._extractVisibleText() || "").slice(0, 90).trim();
            marks.push({
                cfi,
                label: this.chapterEl.textContent || "Bookmark",
                snippet,
                savedAt: new Date().toLocaleDateString(),
            });
            this.bookmarkBtn.innerHTML = `<i class="fas fa-bookmark"></i>`;
        }
        this._writeBookmarks(marks);
        if (!this.marksDrawer.hidden) this._renderMarksDrawer();
    }
    _removeBookmarkByIndex(i) {
        const arr = this._readBookmarks();
        if (i >= 0 && i < arr.length) { arr.splice(i, 1); this._writeBookmarks(arr); }
    }

    /* --- Highlights --- */
    _readHighlights() {
        if (!this.activeCharacterId) return [];
        try { return JSON.parse(localStorage.getItem(LS.highlights(this.activeCharacterId, this.activeBookKey)) || "[]"); }
        catch (_) { return []; }
    }
    _writeHighlights(arr) {
        if (!this.activeCharacterId) return;
        try { localStorage.setItem(LS.highlights(this.activeCharacterId, this.activeBookKey), JSON.stringify(arr)); } catch (_) {}
        this._saveReadingStateToAccount();
    }

    /**
     * Scholar's Journal / cross-device reading state. EPUB location, progress,
     * highlights and bookmarks already live in localStorage (anon fallback);
     * these helpers mirror them to the signed-in account via /api/reading-state
     * so reading carries across devices. Account wins on open (union for
     * highlights/bookmarks so local marks are never lost). Silent no-op when
     * signed out (window._labAuthFetch returns null).
     */
    _saveReadingStateToAccount() {
        if (!this.activeCharacterId || typeof window._labAuthFetch !== "function") return;
        clearTimeout(this._readSyncTimer);
        this._readSyncTimer = setTimeout(() => {
            const cid = this.activeCharacterId, bk = this.activeBookKey;
            let progress = 0;
            try { progress = parseFloat(localStorage.getItem(LS.progress(cid, bk)) || "0") || 0; } catch (_) {}
            const body = {
                characterId: cid,
                bookKey: bk,
                location: (function () { try { return localStorage.getItem(LS.loc(cid, bk)) || ""; } catch (_) { return ""; } })(),
                progress,
                highlights: this._readHighlights(),
                bookmarks: this._readBookmarks(),
            };
            try {
                window._labAuthFetch("/api/reading-state", { method: "POST", body: JSON.stringify(body) }).catch(() => {});
            } catch (_) {}
        }, 800);
    }
    async _loadReadingStateFromAccount() {
        if (!this.activeCharacterId || typeof window._labAuthFetch !== "function") return;
        const cid = this.activeCharacterId, bk = this.activeBookKey;
        let data = null;
        try {
            data = await window._labAuthFetch(
                `/api/reading-state?characterId=${encodeURIComponent(cid)}&bookKey=${encodeURIComponent(bk)}`,
                { method: "GET" }
            );
        } catch (_) { return; }
        const st = data && data.state;
        if (!st) { this._saveReadingStateToAccount(); return; } // seed account from local
        try {
            // Location/progress: account wins (cross-device resume).
            if (st.location) localStorage.setItem(LS.loc(cid, bk), st.location);
            if (typeof st.progress === "number" && isFinite(st.progress)) {
                localStorage.setItem(LS.progress(cid, bk), String(Math.max(0, Math.min(1, st.progress))));
            }
            // Highlights/bookmarks: union so neither account nor local marks are lost.
            const unionBy = (a, b, keyFn) => {
                const out = [], seen = new Set();
                [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])].forEach((it) => {
                    const k = keyFn(it);
                    if (!k || seen.has(k)) return;
                    seen.add(k); out.push(it);
                });
                return out;
            };
            const hl = unionBy(st.highlights, this._readHighlights(), (h) => h && h.cfiRange);
            const bm = unionBy(st.bookmarks, this._readBookmarks(), (m) => m && m.cfi);
            localStorage.setItem(LS.highlights(cid, bk), JSON.stringify(hl));
            localStorage.setItem(LS.marks(cid, bk), JSON.stringify(bm));
        } catch (_) {}
    }
    /** Stable per-book key (characterId is added by the LS helper). */
    _bookKey(meta) {
        if (!meta) return "";
        const raw = meta.id || meta.url || meta.file || meta.filename || meta.title || "";
        return String(raw).replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 120);
    }
    /**
     * Earlier builds stored reading location, bookmarks and highlights
     * per-character (no book key). When a character has exactly one book, that
     * legacy data unambiguously belongs to it — move it under the new per-book
     * key. Multi-book characters are left untouched to avoid misattributing
     * one book's position/marks/highlights onto another. The guard (exactly one
     * book) is checked once the book list resolves, so this stays a safe,
     * idempotent one-time migration.
     */
    _migrateLegacyPerCharacterData(cid) {
        try {
            const moves = [
                { legacy: `epubReader:loc:${cid}`,        next: LS.loc(cid, this.activeBookKey) },
                { legacy: `epubReader:bookmarks:${cid}`,  next: LS.marks(cid, this.activeBookKey) },
                { legacy: `epubReader:highlights:${cid}`, next: LS.highlights(cid, this.activeBookKey) },
            ].filter((m) => m.legacy !== m.next && localStorage.getItem(m.legacy) != null);
            if (!moves.length) return;
            Promise.resolve(this._booksByCharacter && this._booksByCharacter.get(cid))
                .then((list) => {
                    if (!Array.isArray(list) || list.length !== 1) return;
                    moves.forEach((m) => {
                        const raw = localStorage.getItem(m.legacy);
                        if (raw == null) return;
                        if (localStorage.getItem(m.next) == null) localStorage.setItem(m.next, raw);
                        localStorage.removeItem(m.legacy);
                    });
                })
                .catch(() => {});
        } catch (_) {}
    }
    _onSelection(cfiRange, contents) {
        this._lastSelection = { cfiRange, contents };
        try {
            const sel = contents.window.getSelection();
            const text = sel ? sel.toString().trim() : "";
            this._lastSelection.text = text;
            if (text) {
                this._showSelectionBar(sel, contents);
                this._publishContext({ initial: false });
            } else {
                this._hideSelectionBar();
            }
        } catch (_) {}
    }
    /**
     * Map a rect from inside a rendition iframe into stage coordinates. In
     * two-page (spread) layouts there are multiple iframes, so prefer the
     * frame the selection/highlight actually lives in (`contents`) and only
     * fall back to the first iframe.
     */
    _iframeRectToStage(rect, contents) {
        let iframe = null;
        try {
            if (contents && contents.window && contents.window.frameElement) {
                iframe = contents.window.frameElement;
            }
        } catch (_) {}
        if (!iframe) iframe = this.areaEl.querySelector("iframe");
        const fr = iframe ? iframe.getBoundingClientRect() : { left: 0, top: 0 };
        const stageRect = this.stageEl.getBoundingClientRect();
        return {
            cx:     (fr.left - stageRect.left) + rect.left + rect.width / 2,
            top:    (fr.top  - stageRect.top)  + rect.top,
            bottom: (fr.top  - stageRect.top)  + rect.bottom,
        };
    }
    _showSelectionBar(sel, contents) {
        try {
            const range = sel.getRangeAt(0);
            const m = this._iframeRectToStage(range.getBoundingClientRect(), contents);
            this._lastSelAnchor = m;   // reused to anchor the note popover
            const stageW = this.stageEl.clientWidth || 0;
            const cx = Math.max(96, Math.min(m.cx, stageW - 96));
            this.selectionBar.style.left = `${cx}px`;
            this.selectionBar.style.top  = `${Math.max(48, m.top - 10)}px`;
            this.selectionBar.hidden = false;
        } catch (e) { console.warn("[EpubReader] selection bar position failed", e); }
    }
    _hideSelectionBar() {
        this.selectionBar.hidden = true;
    }
    _clearSelection() {
        try {
            const contents = (this.rendition.getContents() || [])[0];
            if (contents && contents.window) contents.window.getSelection().removeAllRanges();
        } catch (_) {}
        this._lastSelection = null;
        this._hideSelectionBar();
        this._publishContext({ initial: false });
    }
    async _addHighlight(colorId, { withNote = false } = {}) {
        if (!this._lastSelection || !this._lastSelection.cfiRange) return;
        const { cfiRange, text } = this._lastSelection;
        const swatch = HIGHLIGHT_COLORS.find(c => c.id === colorId) || HIGHLIGHT_COLORS[0];
        const arr = this._readHighlights();
        const entry = {
            cfiRange,
            color: swatch.id,
            text: (text || "").slice(0, 200),
            note: "",
            chapter: this._lastChapter || "",
            savedAt: new Date().toLocaleDateString(),
        };
        arr.push(entry);
        this._writeHighlights(arr);

        // Scholar's Journal: record a highlight (and a note if one is attached).
        try { window.logJournalActivity?.('book_highlight', { meta: { book: this.activeBookKey } }); } catch (e) {}

        try {
            this.rendition.annotations.add(
                "highlight", cfiRange, { id: `hl-${arr.length - 1}` },
                () => this._openNoteEditor(cfiRange),
                "epubjs-hl",
                { fill: swatch.color, "fill-opacity": "0.4", "mix-blend-mode": "multiply" }
            );
        } catch (e) { console.warn("[EpubReader] add highlight failed", e); }

        // Anchor a note editor before we drop the selection (anchor uses the
        // selection rect; clearing the selection collapses it).
        const anchor = this._lastSelAnchor;
        this._clearSelection();
        if (withNote) this._openNoteEditor(cfiRange, anchor);
        if (!this.marksDrawer.hidden) this._renderMarksDrawer();
    }
    _removeHighlightByIndex(i) {
        const arr = this._readHighlights();
        if (i < 0 || i >= arr.length) return;
        const entry = arr[i];
        try { this.rendition.annotations.remove(entry.cfiRange, "highlight"); } catch (_) {}
        if (this._activeAnnotations) this._activeAnnotations.delete(entry.cfiRange);
        arr.splice(i, 1);
        this._writeHighlights(arr);
    }
    _addHighlightAnnotation(h) {
        if (!this._activeAnnotations) this._activeAnnotations = new Set();
        if (this._activeAnnotations.has(h.cfiRange)) return;
        const swatch = HIGHLIGHT_COLORS.find(c => c.id === h.color) || HIGHLIGHT_COLORS[0];
        try {
            this.rendition.annotations.add(
                "highlight", h.cfiRange, {},
                () => this._openNoteEditor(h.cfiRange),
                "epubjs-hl",
                { fill: swatch.color, "fill-opacity": "0.4", "mix-blend-mode": "multiply" }
            );
            this._activeAnnotations.add(h.cfiRange);
        } catch (_) {}
    }
    _loadAndReplayAllHighlights() {
        this._readHighlights().forEach((h) => this._addHighlightAnnotation(h));
    }
    _replayHighlightsForSection(section) {
        if (!this.rendition || !section) return;
        const sectionHref = (section.href || "").split("#")[0];
        this._readHighlights().forEach((h) => {
            const inSection = sectionHref && h.cfiRange && h.cfiRange.indexOf(sectionHref) !== -1;
            if (!inSection && sectionHref) return;
            this._addHighlightAnnotation(h);
        });
    }
    _flashSearchHit(cfi) {
        if (!cfi || !this.rendition || !this.rendition.annotations) return;
        try {
            this.rendition.annotations.add("highlight", cfi, { id: "search-flash" }, null, "epubjs-search-flash", {
                fill: "#ffd54a", "fill-opacity": "0.55", "mix-blend-mode": "multiply",
            });
            clearTimeout(this._searchFlashTimer);
            this._searchFlashTimer = setTimeout(() => {
                try { this.rendition.annotations.remove(cfi, "highlight"); } catch (_) {}
            }, 2200);
        } catch (_) {}
    }
    /* --- Notes: inline editor anchored under a highlight --- */
    _openNoteEditor(cfiRange, anchor) {
        if (!this.notePopover || !cfiRange) return;
        this._noteTargetCfi = cfiRange;
        const entry = this._readHighlights().find((h) => h.cfiRange === cfiRange);
        this.noteInput.value = (entry && entry.note) || "";
        // Position under the highlight: prefer an explicit anchor (just-created
        // highlight), else the clicked mark's rect, else the last selection.
        const a = anchor || this._markAnchor(cfiRange) || this._lastSelAnchor;
        if (a) {
            const stageW = this.stageEl.clientWidth || 0;
            const cx = Math.max(150, Math.min(a.cx, stageW - 150));
            this.notePopover.style.left = `${cx}px`;
            this.notePopover.style.top  = `${(a.bottom || a.top || 0) + 8}px`;
        }
        this.notePopover.hidden = false;
        setTimeout(() => { try { this.noteInput.focus(); } catch (_) {} }, 40);
    }
    /** Best-effort stage-coords anchor for an existing highlight's mark. */
    _markAnchor(cfiRange) {
        try {
            const all = this.rendition.getContents() || [];
            for (const contents of all) {
                const doc = contents && contents.document;
                if (!doc) continue;
                const g = doc.querySelector(`g[data-epubcfi="${cfiRange}"] rect, svg [data-epubcfi="${cfiRange}"]`);
                if (g && g.getBoundingClientRect) return this._iframeRectToStage(g.getBoundingClientRect(), contents);
            }
        } catch (_) {}
        return null;
    }
    _closeNoteEditor() {
        if (this.notePopover) this.notePopover.hidden = true;
        this._noteTargetCfi = null;
    }
    _saveNote() {
        const cfi = this._noteTargetCfi;
        if (!cfi) { this._closeNoteEditor(); return; }
        const val = (this.noteInput.value || "").trim();
        const arr = this._readHighlights();
        const entry = arr.find((h) => h.cfiRange === cfi);
        if (entry) {
            entry.note = val;
            this._writeHighlights(arr);
            if (this.marksDrawer && !this.marksDrawer.hidden) this._renderMarksDrawer();
            // Scholar's Journal: record a note only when text was actually saved.
            if (val) { try { window.logJournalActivity?.('book_note', { meta: { book: this.activeBookKey } }); } catch (e) {} }
        }
        this._closeNoteEditor();
        this._toast(val ? "Note saved" : "Note cleared");
    }

    /* --- Copy / quote with toast confirmation --- */
    _copySelection() {
        const text = this._lastSelection && this._lastSelection.text;
        if (!text) return;
        this._writeClipboard(text);
        this._clearSelection();
        this._toast("Copied");
    }
    _quoteSelection() {
        const text = this._lastSelection && this._lastSelection.text;
        if (!text) return;
        const meta = this.activeBookMeta || {};
        const attribution = [meta.title, meta.author].filter(Boolean).join(" — ");
        const quote = attribution ? `“${text}”\n— ${attribution}` : `“${text}”`;
        this._writeClipboard(quote);
        this._clearSelection();
        this._toast("Quote copied");
    }
    /**
     * Send the highlighted passage to the current character as a chat message
     * (text + voice). The book context (title/author/selection) is already
     * published to window.ActiveBookContext by _onSelection, so the chat
     * pipeline attaches it automatically; we also inline the passage in the
     * visible message so the avatar (and the user's thread) always have it
     * regardless of context timing.
     */
    _askAvatarAboutSelection() {
        const raw = (this._lastSelection && this._lastSelection.text) || "";
        const text = raw.trim();
        if (!text) return;
        const cc = window.chatController;
        if (!cc || typeof cc.sendMessage !== "function") {
            this._toast("Chat unavailable");
            return;
        }
        const meta = this.activeBookMeta || {};
        const where = meta.title ? `“${meta.title}”` : "this book";
        const snippet = text.length > 600 ? text.slice(0, 600) + "…" : text;
        const prompt = `I just highlighted this passage from ${where}:\n\n“${snippet}”\n\nCan you explain what it means?`;
        // Refresh the published context so the selection rides along with the
        // request (ask:true lets the backend treat it as a direct question).
        try { this._publishContext({ initial: false, ask: true }); } catch (_) {}
        // Avoid two voices at once — stop read-aloud before the avatar replies.
        if (this._readAloud && this._readAloud.state === "playing" && this.stopReadAloud) {
            try { this.stopReadAloud(); } catch (_) {}
        }
        try { cc.sendMessage(prompt); } catch (_) {}
        const who = (meta.characterName || "").trim();
        this._toast(who ? `Asked ${who}` : "Sent to the avatar");
        this._clearSelection();
    }
    _writeClipboard(text) {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).catch(() => this._execCopyFallback(text));
            } else {
                this._execCopyFallback(text);
            }
        } catch (_) { this._execCopyFallback(text); }
    }
    _execCopyFallback(text) {
        try {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            ta.remove();
        } catch (_) {}
    }
    _toast(msg) {
        if (!this.toastEl) return;
        const em = this.toastEl.querySelector("em");
        if (em) em.textContent = msg || "Copied";
        const a = this._lastSelAnchor;
        const stageW = this.stageEl.clientWidth || 0;
        const cx = a ? Math.max(80, Math.min(a.cx, stageW - 80)) : (stageW / 2);
        this.toastEl.style.left = `${cx}px`;
        this.toastEl.style.top  = a ? `${Math.max(8, (a.top || 0) - 8)}px` : "16px";
        this.toastEl.classList.add("is-visible");
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => this.toastEl.classList.remove("is-visible"), 1600);
    }

    _onRelocated(location) {
        if (!location || !location.start) return;
        const cfi = location.start.cfi;
        if (cfi && this.activeCharacterId) {
            try { localStorage.setItem(LS.loc(this.activeCharacterId, this.activeBookKey), cfi); } catch (_) {}
            // Scholar's Journal: record "reading" once per chapter change (not on
            // every page turn) so a long read isn't logged as hundreds of events.
            try {
                const href = (location.start.href || "").split("#")[0];
                if (href && href !== this._lastJournalHref) {
                    if (this._lastJournalHref) {
                        window.logJournalActivity?.('book_read', { meta: { book: this.activeBookKey } });
                    }
                    this._lastJournalHref = href;
                }
            } catch (_) {}
            // Mirror the new position to the signed-in account (debounced).
            this._saveReadingStateToAccount();
            // Persist whole-book progress (0..1) so library cards can show a
            // per-book progress bar without re-opening the EPUB.
            try {
                if (this.locationsReady && this.book && this.book.locations) {
                    const f = this.book.locations.percentageFromCfi(cfi);
                    if (typeof f === "number" && isFinite(f)) {
                        localStorage.setItem(
                            LS.progress(this.activeCharacterId, this.activeBookKey),
                            String(Math.max(0, Math.min(1, f)))
                        );
                    }
                }
            } catch (_) {}
        }
        // Bookmark icon state
        const marks = this._readBookmarks();
        const isMarked = marks.some((m) => m.cfi === cfi);
        if (this.bookmarkBtn) {
            this.bookmarkBtn.innerHTML = isMarked ? `<i class="fas fa-bookmark"></i>` : `<i class="far fa-bookmark"></i>`;
        }
        this._updateFooter(location);
        this._updateScrubber(location);
        this._emitRelocated(location);
        this._publishContext({ initial: false });
        if (this._readAloud.state === "playing" || this._readAloud.state === "paused") {
            this.stopReadAloud();
        }
    }

    _updateFooter(location) {
        if (!location || !location.start) return;
        // Resolve chapter label from TOC
        let chapter = "";
        try {
            const href = (location.start.href || "").split("#")[0];
            const items = this._tocItems || [];
            const match = items.find((it) => (it.href || "").split("#")[0] === href);
            if (match) chapter = match.label || "";
        } catch (_) {}
        this.chapterEl.textContent = chapter || "—";

        let pct = 0, locText = "—";
        try {
            if (this.locationsReady && this.book && this.book.locations && location.start.cfi) {
                pct = Math.round((this.book.locations.percentageFromCfi(location.start.cfi) || 0) * 100);
                const cur = location.start.location || this.book.locations.locationFromCfi(location.start.cfi);
                const total = this.book.locations.length();
                locText = `Page ${cur || "—"} of ${total || "—"} · ${pct}%`;
            } else if (location.start.displayed) {
                const d = location.start.displayed;
                locText = `Page ${d.page || "—"} of ${d.total || "—"}`;
            }
        } catch (_) {}
        this.locEl.textContent = locText;
        this._lastLocText = locText;
        this._lastChapter = chapter;
        this._lastPct = pct;

        try {
            const fullSecText = this._extractCurrentSectionText() || "";
            const visText = this._extractVisibleText() || "";
            let remaining = fullSecText;
            if (visText) {
                const idx = fullSecText.indexOf(visText.slice(0, Math.min(60, visText.length)));
                if (idx >= 0) remaining = fullSecText.slice(idx + visText.length);
            }
            const sectionWords = (remaining.match(/\S+/g) || []).length;
            const curIndex = (location.start && typeof location.start.index === "number") ? location.start.index : -1;
            this._lastSectionWordsLeft = sectionWords;
            this._lastSpineIndex = curIndex;
            this._renderTimeLeft(sectionWords, curIndex);
            // Kick off (once) a whole-book word-count so we can show time-left
            // for the entire book, not just the current chapter.
            this._ensureSectionWordCounts();
        } catch (_) { this.timeLeftEl.textContent = ""; }
    }
    /** Format a minute count as "X hr Y min" / "Z min". */
    _fmtMins(mins) {
        mins = Math.max(0, Math.round(mins));
        if (mins < 1) return "<1 min";
        if (mins < 60) return `${mins} min`;
        const h = Math.floor(mins / 60), m = mins % 60;
        return m ? `${h} hr ${m} min` : `${h} hr`;
    }
    /** Render chapter time-left plus whole-book time-left (when known). */
    _renderTimeLeft(sectionWords, curIndex) {
        if (!this.timeLeftEl) return;
        const chapMins = Math.max(0, Math.round(sectionWords / READING_WPM));
        let txt = chapMins > 0 ? `~${this._fmtMins(chapMins)} left in chapter` : "End of chapter";
        try {
            const counts = this._sectionWordCounts;
            if (counts && curIndex >= 0) {
                let bookWordsLeft = sectionWords;
                for (let i = curIndex + 1; i < counts.length; i++) {
                    if (typeof counts[i] === "number") bookWordsLeft += counts[i];
                }
                const bookMins = Math.round(bookWordsLeft / READING_WPM);
                if (this._sectionWordCountsDone && bookMins > 0 && bookMins >= chapMins) {
                    txt += ` · ~${this._fmtMins(bookMins)} left in book`;
                }
            }
        } catch (_) {}
        this.timeLeftEl.textContent = txt;
    }
    /** Clear the per-book whole-book ETA cache (call on each book open/close). */
    _resetBookWordCounts() {
        this._sectionWordCounts = null;
        this._sectionWordCountsDone = false;
        this._sectionWordCountsComputing = false;
        this._lastSectionWordsLeft = null;
        this._lastSpineIndex = -1;
        // Invalidate any in-flight async counting from a previous book.
        this._bookWordCountToken = (this._bookWordCountToken || 0) + 1;
    }
    /** Lazily load every spine section once and cache its word count. */
    async _ensureSectionWordCounts() {
        if (this._sectionWordCountsDone || this._sectionWordCountsComputing) return;
        const spineItems = (this.book && this.book.spine && this.book.spine.spineItems) || [];
        if (!spineItems.length) return;
        // Per-open token: if the user switches books mid-count, this run's late
        // writes are discarded so stale counts can't leak into the new book.
        const token = this._bookWordCountToken || 0;
        this._sectionWordCountsComputing = true;
        const counts = new Array(spineItems.length).fill(0);
        this._sectionWordCounts = counts;
        try {
            for (let i = 0; i < spineItems.length; i++) {
                if (token !== (this._bookWordCountToken || 0)) return; // superseded
                const item = spineItems[i];
                try {
                    await item.load(this.book.load.bind(this.book));
                    const doc = item.document;
                    const text = (doc && doc.body) ? (doc.body.textContent || "") : "";
                    counts[i] = (text.match(/\S+/g) || []).length;
                    item.unload();
                } catch (_) { counts[i] = 0; }
            }
            if (token !== (this._bookWordCountToken || 0)) return; // superseded
            this._sectionWordCountsDone = true;
            // Re-render with the now-known whole-book figure.
            if (typeof this._lastSectionWordsLeft === "number") {
                this._renderTimeLeft(this._lastSectionWordsLeft, this._lastSpineIndex);
            }
        } catch (_) {
        } finally {
            if (token === (this._bookWordCountToken || 0)) {
                this._sectionWordCountsComputing = false;
            }
        }
    }
    _updateScrubber(location) {
        if (!this.scrubberEl) return;
        if (this.locationsReady && location && location.start && location.start.cfi) {
            const pct = (this.book.locations.percentageFromCfi(location.start.cfi) || 0);
            this.scrubberEl.value = String(Math.round(pct * 1000));
            this.scrubberEl.title = `${Math.round(pct * 100)}%`;
        }
    }
    _scrubberPreview() {
        if (!this.locationsReady || !this.book) return;
        const pct = parseInt(this.scrubberEl.value, 10) / 1000;
        let chapterLabel = "";
        try {
            const cfi = this.book.locations.cfiFromPercentage(pct);
            const spineItem = this.book.spine && this.book.spine.get(cfi);
            const href = spineItem && (spineItem.href || spineItem.url || "");
            if (href && this._tocByHref) {
                chapterLabel = this._tocByHref[href.split("#")[0]] || "";
            }
        } catch (_) {}
        const label = chapterLabel
            ? `${chapterLabel} — ${Math.round(pct * 100)}%`
            : `${Math.round(pct * 100)}%`;
        this.scrubberEl.title = label;
        const tip = this.tipEl;
        if (tip && this.scrubberEl.parentElement) {
            tip.hidden = false;
            tip.textContent = label;
            const r = this.scrubberEl.getBoundingClientRect();
            const x = r.width * pct;
            const tw = tip.offsetWidth || 80;
            tip.style.left = `${Math.max(0, Math.min(r.width - tw, x - tw / 2))}px`;
            clearTimeout(this._tipHide);
            this._tipHide = setTimeout(() => { tip.hidden = true; }, 1100);
        }
    }
    _scrubberCommit() {
        if (!this.locationsReady || !this.book || !this.rendition) return;
        const pct = parseInt(this.scrubberEl.value, 10) / 1000;
        try {
            const cfi = this.book.locations.cfiFromPercentage(pct);
            if (cfi) this.rendition.display(cfi);
        } catch (_) {}
    }

    _extractCurrentSectionText() {
        try {
            const contents = this.rendition.getContents() || [];
            for (const c of contents) {
                const doc = c && c.document;
                if (doc && doc.body) {
                    return (doc.body.innerText || doc.body.textContent || "").replace(/\s+/g, " ").trim();
                }
            }
        } catch (_) {}
        return "";
    }
    _logFallback(reason) {
        try {
            this._fallbackCount = (this._fallbackCount || 0) + 1;
            if (this._fallbackCount <= 3 || this._fallbackCount % 10 === 0) {
                console.warn(`[EpubReader] visible-text fell back to section text (#${this._fallbackCount}): ${reason}`);
            }
        } catch (_) {}
    }
    _extractVisibleText() {
        let reason = "no-contents";
        try {
            const loc = this.rendition.currentLocation();
            if (!loc || !loc.start || !loc.end) {
                this._logFallback("no-current-location");
                return this._extractCurrentSectionText();
            }
            const startCfi = loc.start.cfi, endCfi = loc.end.cfi;
            const contents = this.rendition.getContents() || [];
            for (const c of contents) {
                const doc = c && c.document;
                if (!doc || !doc.body) { reason = "no-doc"; continue; }
                const getRange = (cfi) => {
                    try { if (typeof c.range === "function") return c.range(cfi); } catch (_) {}
                    try { if (this.rendition && typeof this.rendition.getRange === "function") return this.rendition.getRange(cfi); } catch (_) {}
                    try { if (this.book && typeof this.book.getRange === "function") {
                        const r = this.book.getRange(cfi);
                        if (r && typeof r.then !== "function") return r;
                    } } catch (_) {}
                    return null;
                };
                try {
                    const startRange = getRange(startCfi);
                    const endRange   = getRange(endCfi);
                    if (!startRange || !endRange) { reason = "no-range"; continue; }
                    const merged = doc.createRange();
                    merged.setStart(startRange.startContainer, startRange.startOffset);
                    merged.setEnd(endRange.endContainer, endRange.endOffset);
                    const txt = (merged.toString() || "").replace(/\s+/g, " ").trim();
                    if (txt) return txt;
                    reason = "empty-range-text";
                } catch (e) { reason = "range-merge-error"; }
            }
        } catch (e) { reason = "extract-exception"; }
        this._logFallback(reason);
        return this._extractCurrentSectionText();
    }
    _surroundingContext(visibleText, fullText, padChars = 220) {
        if (!visibleText || !fullText) return { prevTail: "", nextHead: "" };
        const probe = visibleText.slice(0, Math.min(80, visibleText.length));
        const idx = fullText.indexOf(probe);
        if (idx < 0) return { prevTail: "", nextHead: "" };
        const prevTail = fullText.slice(Math.max(0, idx - padChars), idx).trim();
        const after = idx + visibleText.length;
        const nextHead = fullText.slice(after, Math.min(fullText.length, after + padChars)).trim();
        return { prevTail, nextHead };
    }
    _hashString(s) {
        let h = 0x811c9dc5;
        for (let i = 0; i < s.length; i++) {
            h ^= s.charCodeAt(i);
            h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
        }
        return h.toString(16);
    }
    _publishContext({ initial, ask } = {}) {
        if (!this.rendition || !this.activeBookMeta) return;
        const visible = this._extractVisibleText();
        const sectionFull = this._extractCurrentSectionText();
        const { prevTail, nextHead } = this._surroundingContext(visible, sectionFull, 240);

        let excerpt = visible || "";
        const MAX = 6000;
        if (excerpt.length > MAX) excerpt = excerpt.slice(0, MAX) + "…";

        const selection = (this._lastSelection && this._lastSelection.text) || "";
        const nowReading = this._readAloud.state === "playing"
            ? (this._readAloud.queue[this._readAloud.index] || {}).text || ""
            : "";

        const cfi = (() => {
            try { const l = this.rendition.currentLocation(); return l && l.start ? l.start.cfi : ""; } catch (_) { return ""; }
        })();

        const cfiKey = `${cfi}|${selection}|${nowReading}`;
        const pageUnchanged = (cfiKey === this._lastCfiKey) && !initial;

        const ctx = {
            title: this.activeBookMeta.title,
            author: this.activeBookMeta.author,
            character_id: this.activeBookMeta.characterId,
            chapter: this._lastChapter || "",
            location: this._lastLocText || "",
            progress_pct: this._lastPct || 0,
            excerpt,
            prev_tail: prevTail,
            next_head: nextHead,
            selection,
            now_reading: nowReading,
            page_hash: this._hashString(cfiKey),
            page_unchanged: pageUnchanged,
            ask: !!ask,
            updatedAt: Date.now(),
        };
        this._lastCfiKey = cfiKey;

        window.ActiveBookContext = ctx;
        clearTimeout(this._clearCtxTimer);
        window.dispatchEvent(new CustomEvent("bookContextChanged", { detail: { ...ctx, initial: !!initial } }));
    }

    _updatePlayButton() {
        if (!this.playBtn) return;
        const cc = window.chatController;
        const rtts = cc && cc.realtimeTTS;
        const hasRtts = !!rtts;
        const ourState = this._readAloud.state;
        let avatarBusy = false;
        if (hasRtts && ourState === "idle") {
            try {
                avatarBusy = !!(
                    (typeof window !== "undefined" && window.TTSActive) ||
                    rtts.playing ||
                    (typeof rtts.isSpeaking === "function" && rtts.isSpeaking())
                );
            } catch (_) {}
        }
        this.playBtn.disabled = !hasRtts || avatarBusy;
        this.playBtn.title = !hasRtts
            ? "Read-aloud is unavailable while the avatar is initializing"
            : avatarBusy
                ? "Wait for the avatar to finish speaking"
                : "Read this page aloud (Space)";
        if (this.stopBtn) this.stopBtn.disabled = !hasRtts || ourState === "idle";
    }
    async _togglePlay() {
        if (this._readAloud.state === "playing") {
            this._pauseReadAloud();
            return;
        }
        if (this._readAloud.state === "paused") {
            this._resumeReadAloud();
            return;
        }
        await this._startReadAloud();
    }
    _setPlayUI(state) {
        const i = this.playBtn.querySelector("i");
        if (state === "playing") {
            this.playBtn.classList.add("is-playing");
            i.className = "fas fa-pause";
            this.playBtn.title = "Pause (Space)";
        } else if (state === "paused") {
            this.playBtn.classList.remove("is-playing");
            i.className = "fas fa-play";
            this.playBtn.title = "Resume (Space)";
        } else {
            this.playBtn.classList.remove("is-playing");
            i.className = "fas fa-play";
            this.playBtn.title = "Read this page aloud (Space)";
        }
        if (this.stopBtn) this.stopBtn.disabled = (state === "idle");
        // Notify the embedded panel-head button (its own .er-play is hidden).
        try {
            window.dispatchEvent(new CustomEvent("epubReadAloudState", { detail: { state } }));
        } catch (_) {}
    }
    async _startReadAloud() {
        const cc = window.chatController;
        const rtts = cc && cc.realtimeTTS;
        if (!rtts) {
            console.warn("[EpubReader] No realtimeTTS available — cannot read aloud");
            return;
        }
        const visible = this._extractVisibleText();
        if (!visible) return;
        const sentences = this._splitSentences(visible);
        if (!sentences.length) return;

        const queue = [];
        try {
            const contents = (this.rendition.getContents() || [])[0];
            const doc = contents && contents.document;
            if (doc) {
                let cursor = 0;
                const fullText = (doc.body.innerText || doc.body.textContent || "");
                for (const s of sentences) {
                    const range = this._findTextRange(doc, s, cursor);
                    if (range) {
                        cursor = (range._endIndexHint != null) ? range._endIndexHint : cursor;
                    }
                    queue.push({ text: s, range });
                }
            } else {
                sentences.forEach(s => queue.push({ text: s, range: null }));
            }
        } catch (_) {
            sentences.forEach(s => queue.push({ text: s, range: null }));
        }

        const cfi = (() => {
            try { const l = this.rendition.currentLocation(); return l && l.start ? l.start.cfi : ""; } catch (_) { return ""; }
        })();

        this._readAloud = { state: "playing", queue, index: 0, cfi };
        this._setPlayUI("playing");
        this._publishContext({ initial: false });
        await this._playFromCurrentSentence();
    }
    async _playFromCurrentSentence() {
        const cc = window.chatController;
        const rtts = cc && cc.realtimeTTS;
        if (!rtts) { this.stopReadAloud(); return; }

        // Single-flight guard: a pause→resume can start a new loop while a prior
        // loop is still unwinding its in-flight `await _speakSentence`. Each loop
        // captures a loopId; only the newest loop may touch index/state.
        const myLoopId = (this._readAloud.loopId = (this._readAloud.loopId || 0) + 1);
        const owns = () => this._readAloud.loopId === myLoopId;

        while (owns() && this._readAloud.state === "playing" && this._readAloud.index < this._readAloud.queue.length) {
            const item = this._readAloud.queue[this._readAloud.index];
            this._highlightSentence(item.range);
            const wordEntries = this._buildWordEntries(item);
            this._publishContext({ initial: false });
            try {
                await this._speakSentence(item.text, rtts, cc, wordEntries);
            } catch (e) {
                console.warn("[EpubReader] read-aloud sentence failed", e);
                break;
            }
            if (!owns() || this._readAloud.state !== "playing") break;
            this._readAloud.index += 1;
        }
        if (owns() && this._readAloud.state === "playing") {
            this.stopReadAloud();
        }
    }
    async _speakSentence(text, rtts, cc, wordEntries) {
        if (!text || !text.trim()) return;
        try {
            if (typeof rtts.ensureReadyForNextResponse === "function") {
                await rtts.ensureReadyForNextResponse(120);
            }
            if (rtts.audioCtx && rtts.audioCtx.state !== "running") {
                await rtts.audioCtx.resume();
            }
            rtts.start();
            if (typeof rtts.clearStopGuard === "function") rtts.clearStopGuard();
            if (cc && cc.animationController) cc.animationController.onTTSStart();
        } catch (e) { console.warn("[EpubReader] start TTS failed", e); }

        // Each sentence gets a monotonically increasing run token. The
        // audio-start/audio-end events are GLOBAL to the pipeline (not stamped
        // per turn), so we guard every listener and timer against the current
        // token to keep a lingering event/timer from a previous (or canceled)
        // sentence from settling this one. Bumped on pause/stop/page-turn too.
        const myToken = (this._readAloud.runToken = (this._readAloud.runToken || 0) + 1);
        const isCurrent = () => this._readAloud.runToken === myToken;

        // Word follow-along: ElevenLabs gives us no per-word audio timestamps, so
        // we start a char-length-weighted karaoke pacer when audio actually begins
        // (the "audio-start" event) and stop it when the sentence's audio ends.
        let pacerStarted = false;
        const startPacer = () => {
            if (pacerStarted || !isCurrent()) return;
            pacerStarted = true;
            if (this._readAloud.state === "playing") this._startWordPacer(wordEntries);
        };

        let sawStart = false;
        const done = new Promise((resolve) => {
            let settled = false;
            const registeredAt = performance.now();
            const timers = [];
            const cleanup = () => {
                for (const t of timers) { try { clearTimeout(t); } catch (_) {} }
                try { rtts.off && rtts.off("audio-end", onEnd); } catch (_) {}
                try { rtts.off && rtts.off("audio-start", onStart); } catch (_) {}
                if (this._abortActiveSentence === finish) this._abortActiveSentence = null;
            };
            const finish = () => { if (settled) return; settled = true; cleanup(); resolve(); };
            const onStart = () => { if (isCurrent()) { sawStart = true; startPacer(); } };
            const onEnd = () => {
                if (!isCurrent()) return;
                // Ignore an audio-end that arrives before this sentence's audio has
                // even started AND implausibly early — it almost certainly belongs
                // to the previous turn's teardown, not this one.
                if (!sawStart && performance.now() - registeredAt < 250) return;
                finish();
            };
            // Abort hook: pause/stop/page-turn call this to settle the in-flight
            // sentence immediately (removing listeners/timers) instead of waiting
            // for the fallback timeout — which would leave a stale runner alive.
            this._abortActiveSentence = finish;
            try { rtts.on && rtts.on("audio-end", onEnd); } catch (_) {}
            try { rtts.on && rtts.on("audio-start", onStart); } catch (_) {}
            // Fallback: if "audio-start" never fires (already-active pipeline,
            // throttled tab), start the word pacer shortly after queuing audio.
            timers.push(setTimeout(() => { if (this._readAloud.state === "playing") startPacer(); }, 600));
            // Safety net: if "audio-end" never arrives, cancel this turn (so it
            // can't bleed into the next sentence) and resolve.
            const fallbackMs = Math.min(60000, Math.max(2500, text.length * 90));
            timers.push(setTimeout(() => {
                if (isCurrent() && this._readAloud.state === "playing") {
                    try { rtts.cancel && rtts.cancel(); } catch (_) {}
                }
                finish();
            }, fallbackMs));
        });

        // Task #99: feed the WHOLE sentence in one delta. _speakSentence is
        // already called once per sentence, and the TTS engine segments
        // outbound text on sentence boundaries internally, so there's no
        // need to drip words (which only risked fragmenting a single
        // utterance). Mid-sentence aborts are still handled by the run
        // token + the done promise's listeners + cancel().
        if (this._readAloud.state === "playing" && isCurrent()) {
            try { rtts.handleLLMDelta(text.trim() + " "); } catch (_) {}
        }
        try { rtts.flushAndClose(); } catch (_) {}

        await done;
        this._stopWordPacer();
    }
    // Build per-word DOM ranges within a sentence so the read-aloud highlight can
    // advance word by word. Reuses _findTextRange with an advancing cursor so
    // repeated words resolve to the correct (next) occurrence.
    _buildWordEntries(item) {
        const words = ((item && item.text) || "").split(/\s+/).filter(Boolean);
        let doc = null;
        try {
            const contents = (this.rendition && this.rendition.getContents()) || [];
            const c = contents[0];
            doc = c && c.document;
        } catch (_) {}
        if (!doc || !item || !item.range || item.range._startIndexHint == null) {
            return words.map(w => ({ text: w, range: null }));
        }
        let cursor = item.range._startIndexHint;
        const out = [];
        for (const w of words) {
            let range = null;
            try { range = this._findTextRange(doc, w, cursor); } catch (_) {}
            if (range && range._endIndexHint != null) cursor = range._endIndexHint;
            out.push({ text: w, range });
        }
        return out;
    }
    _startWordPacer(entries) {
        this._stopWordPacer();
        if (!entries || !entries.length) return;
        const totalChars = entries.reduce((a, e) => a + (e.text.length + 1), 0) || 1;
        const estTotalMs = Math.max(600, (totalChars / READ_ALOUD_CHARS_PER_SEC) * 1000);
        let i = 0;
        const step = () => {
            if (this._readAloud.state !== "playing") { this._clearWordHighlight(); return; }
            if (i >= entries.length) { this._wordPacer = null; return; }
            const e = entries[i];
            if (e.range) this._highlightWord(e.range);
            const durMs = Math.max(
                READ_ALOUD_MIN_WORD_MS,
                ((e.text.length + 1) / totalChars) * estTotalMs
            );
            i++;
            this._wordPacer = setTimeout(step, durMs);
        };
        step();
    }
    _stopWordPacer() {
        if (this._wordPacer) { clearTimeout(this._wordPacer); this._wordPacer = null; }
    }
    _highlightWord(range) {
        this._clearWordHighlight();
        if (!range || !this.wordOverlay) return;
        try {
            const rects = range.getClientRects();
            const iframe = this.areaEl.querySelector("iframe");
            const fr = iframe ? iframe.getBoundingClientRect() : { left: 0, top: 0 };
            const stageRect = this.stageEl.getBoundingClientRect();
            const offX = fr.left - stageRect.left;
            const offY = fr.top  - stageRect.top;
            const frag = document.createDocumentFragment();
            for (const r of rects) {
                if (r.width < 1 || r.height < 4) continue;
                const div = document.createElement("div");
                div.className = "er-word-rect";
                div.style.left   = `${offX + r.left}px`;
                div.style.top    = `${offY + r.top}px`;
                div.style.width  = `${r.width}px`;
                div.style.height = `${r.height}px`;
                frag.appendChild(div);
            }
            this.wordOverlay.appendChild(frag);
            this._activeWordRange = range;
        } catch (_) {}
    }
    _clearWordHighlight() {
        if (this.wordOverlay) this.wordOverlay.innerHTML = "";
        this._activeWordRange = null;
    }
    _pauseReadAloud() {
        if (this._readAloud.state !== "playing") return;
        this._readAloud.state = "paused";
        this._readAloud.runToken = (this._readAloud.runToken || 0) + 1;
        try { this._abortActiveSentence && this._abortActiveSentence(); } catch (_) {}
        this._stopWordPacer();
        this._clearWordHighlight();
        const cc = window.chatController;
        const rtts = cc && cc.realtimeTTS;
        try { rtts && rtts.cancel && rtts.cancel(); } catch (_) {}
        try { cc && cc.animationController && cc.animationController.onTTSEnd(); } catch (_) {}
        this._setPlayUI("paused");
    }
    async _resumeReadAloud() {
        if (this._readAloud.state !== "paused") return;
        this._readAloud.state = "playing";
        this._setPlayUI("playing");
        await this._playFromCurrentSentence();
    }
    stopReadAloud() {
        if (this._readAloud.state === "idle") return;
        this._readAloud.state = "stopping";
        const nextToken = (this._readAloud.runToken || 0) + 1;
        try { this._abortActiveSentence && this._abortActiveSentence(); } catch (_) {}
        this._stopWordPacer();
        const cc = window.chatController;
        const rtts = cc && cc.realtimeTTS;
        try { rtts && rtts.cancel && rtts.cancel(); } catch (_) {}
        try { cc && cc.animationController && cc.animationController.onTTSEnd(); } catch (_) {}
        this._readAloud = { state: "idle", queue: [], index: 0, cfi: "", runToken: nextToken };
        this._clearSentenceHighlight();
        this._clearWordHighlight();
        this._setPlayUI("idle");
        this._publishContext({ initial: false });
    }

    _splitSentences(text) {
        if (!text) return [];
        const out = [];
        const re = /[^.!?…]+[.!?…]+["')\]]*|[^.!?…]+$/g;
        let m;
        while ((m = re.exec(text)) !== null) {
            const s = m[0].trim();
            if (s) out.push(s);
        }
        return out;
    }
    _findTextRange(doc, snippet, fromIndex = 0) {
        try {
            const probe = String(snippet || "").replace(/\s+/g, " ").trim();
            if (!probe) return null;
            const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
            const nodes = [];
            let acc = "";
            let node;
            while ((node = walker.nextNode())) {
                const v = node.nodeValue || "";
                nodes.push({ node, start: acc.length, len: v.length });
                acc += v;
            }
            const tokens = probe.split(/\s+/).map(t =>
                t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
            );
            const re = new RegExp(tokens.join("\\s+"));
            const tail = acc.slice(fromIndex);
            const m = re.exec(tail);
            if (!m) return null;
            const idx = fromIndex + m.index;
            const endIdx = idx + m[0].length;
            const startInfo = this._nodeAtOffset(nodes, idx);
            const endInfo   = this._nodeAtOffset(nodes, endIdx);
            if (!startInfo || !endInfo) return null;
            const range = doc.createRange();
            range.setStart(startInfo.node, startInfo.offset);
            range.setEnd(endInfo.node, endInfo.offset);
            range._startIndexHint = idx;
            range._endIndexHint = endIdx;
            return range;
        } catch (_) { return null; }
    }
    _nodeAtOffset(nodes, target) {
        for (let i = 0; i < nodes.length; i++) {
            const n = nodes[i];
            if (target <= n.start + n.len) {
                const off = Math.max(0, Math.min(n.len, target - n.start));
                return { node: n.node, offset: off };
            }
        }
        const last = nodes[nodes.length - 1];
        return last ? { node: last.node, offset: last.len } : null;
    }
    _highlightSentence(range) {
        this._clearSentenceHighlight();
        this._clearWordHighlight();
        if (!range) return;
        try {
            const rects = range.getClientRects();
            const iframe = this.areaEl.querySelector("iframe");
            const fr = iframe ? iframe.getBoundingClientRect() : { left: 0, top: 0 };
            const stageRect = this.stageEl.getBoundingClientRect();
            const offX = fr.left - stageRect.left;
            const offY = fr.top  - stageRect.top;
            const frag = document.createDocumentFragment();
            for (const r of rects) {
                if (r.width < 2 || r.height < 4) continue;
                const div = document.createElement("div");
                div.className = "er-sentence-rect";
                div.style.left   = `${offX + r.left}px`;
                div.style.top    = `${offY + r.top}px`;
                div.style.width  = `${r.width}px`;
                div.style.height = `${r.height}px`;
                frag.appendChild(div);
            }
            this.sentenceOverlay.appendChild(frag);
            this._activeHighlightRange = range;
        } catch (_) {}
    }
    _refreshSentenceHighlightPosition() {
        if (this._activeHighlightRange && this.sentenceOverlay.children.length) {
            this._highlightSentence(this._activeHighlightRange);
        }
        if (this._activeWordRange && this.wordOverlay && this.wordOverlay.children.length) {
            this._highlightWord(this._activeWordRange);
        }
    }
    _clearSentenceHighlight() {
        this.sentenceOverlay.innerHTML = "";
        this._activeHighlightRange = null;
    }

    async _lookupWord(word, mouseEvent) {
        const w = word.toLowerCase().replace(/[^\p{L}\p{N}'-]/gu, "");
        if (!w) return;
        this.dictPopover.innerHTML = `<div class="er-dict-loading">Looking up <em>${this._escape(w)}</em>…</div>`;
        this._positionDictPopover(mouseEvent);
        this.dictPopover.hidden = false;

        let entry = this._dictCache.get(w);
        if (!entry) {
            try {
                const res = await fetch(`https://en.wiktionary.org/api/rest_v1/page/definition/${encodeURIComponent(w)}`);
                if (res.ok) {
                    const j = await res.json();
                    entry = this._normalizeWiktionary(j);
                    this._dictCache.set(w, entry);
                }
            } catch (_) {}
        }
        if (!entry || !entry.defs.length) {
            this.dictPopover.innerHTML = `<div class="er-dict-empty">No definition found for <em>${this._escape(w)}</em>.</div>`;
        } else {
            this.dictPopover.innerHTML = `
                <div class="er-dict-word">${this._escape(w)}</div>
                <ol class="er-dict-defs">${entry.defs.slice(0, 4).map(d => `<li><span class="er-dict-pos">${this._escape(d.pos || "")}</span> ${this._escape(d.def)}</li>`).join("")}</ol>
                <div class="er-dict-foot"><a href="https://en.wiktionary.org/wiki/${encodeURIComponent(w)}" target="_blank" rel="noopener">Wiktionary →</a></div>
            `;
        }
        const close = () => { this.dictPopover.hidden = true; document.removeEventListener("click", close, true); };
        setTimeout(() => document.addEventListener("click", close, true), 0);
    }
    _normalizeWiktionary(j) {
        const defs = [];
        try {
            (j.en || []).forEach(group => {
                (group.definitions || []).forEach(d => {
                    const text = (d.definition || "").replace(/<[^>]+>/g, "").trim();
                    if (text) defs.push({ pos: group.partOfSpeech || "", def: text });
                });
            });
        } catch (_) {}
        return { defs };
    }
    _positionDictPopover(e) {
        try {
            const stageRect = this.stageEl.getBoundingClientRect();
            const iframe = this.areaEl.querySelector("iframe");
            const fr = iframe ? iframe.getBoundingClientRect() : { left: 0, top: 0 };
            const x = (e ? e.clientX : 100) + fr.left - stageRect.left;
            const y = (e ? e.clientY : 100) + fr.top  - stageRect.top + 12;
            this.dictPopover.style.left = `${Math.max(8, Math.min(stageRect.width - 280, x - 140))}px`;
            this.dictPopover.style.top  = `${Math.max(8, y)}px`;
        } catch (_) {}
    }

    _setLoading(on, msg) {
        this.loadingEl.hidden = !on;
        if (on) this.loadingEl.textContent = msg || "Loading…";
    }
    _setError(msg) {
        this.errorEl.hidden = !msg;
        this.errorEl.textContent = msg || "";
    }
    _escape(s) {
        return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => (
            { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
        ));
    }
}

if (typeof window !== "undefined") {
    let _reader = null;
    window.getEpubReader = function getEpubReader() {
        if (!_reader) {
            try { _reader = new EpubReaderController(); }
            catch (e) { console.warn("[EpubReader] init failed", e); _reader = null; }
        }
        return _reader;
    };
}

